import { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense, type ComponentProps } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Bot,
  Key,
  Plus,
  Trash2,
  Settings,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Mic,
  FileText,
  Link,
  Upload,
  BarChart3,
  Zap,
  TrendingUp,
  Clock,
  DollarSign,
  Activity,
  MessageSquare,
  FolderOpen,
  Play,
  Volume2,
  PieChart,
  Database,
  Phone,
  AlertTriangle,
  GitBranch,
  Sparkles,
  Target,
  Trophy,
  Check,
  X,
  Eye,
  ScanSearch,
  Search,
  Code,
  Image,
  UserCircle,
  Brain,
  Shield,
  Star,
  Globe,
  ExternalLink,
  HelpCircle,
  Users,
  Tags,
  Network,
  Server,
  type LucideIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { useEmbedderGate } from '@/hooks/useEmbedderGate';
import EmbedderChoiceWizard from '@/components/EmbedderChoiceWizard';
import { HubShell, type HubTab } from '@/components/common/HubShell';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { TenantUsageBanner } from '@/components/limits/TenantUsageBanner';
import { useChatModelOptions } from '@/hooks/useChatModelOptions';
import TranscriptionsDashboard from '@/components/ai/TranscriptionsDashboard';
// Task #1976 — Custom AI (5° provider OpenAI-compatible tenant-scoped, round A).
import CustomAITab from '@/components/ai/CustomAITab';
import ClaudeAIConfigTab from '@/components/ai/ClaudeAIConfigTab';
import GeminiAIConfigTab from '@/components/ai/GeminiAIConfigTab';
import WorkflowBuilderAgentModal from '@/components/ai/WorkflowBuilderAgentModal';
import CommissioningAgentConfigModal from '@/components/ai/CommissioningAgentConfigModal';
import ChatbotConfigModal from '@/components/ai/ChatbotConfigModal';
import ChatbotEmbedModal from '@/components/ai/ChatbotEmbedModal';
import PersonalAssistantConfigModal from '@/components/ai/PersonalAssistantConfigModal';
import ElevenLabsConfigTab from '@/components/ai/ElevenLabsConfigTab';
import ElevenLabsAgentConfigModal from '@/components/ai/ElevenLabsAgentConfigModal';
import DashboardOverview from '@/components/ai/DashboardOverview';
import KBMetadataSchemaPage from '@/pages/admin/KBMetadataSchemaPage';
import ServiceRoutingTab from '@/components/ai/ServiceRoutingTab';
// Task #2142 — Provider matrix per la tab Routing AI (cache+socket+optimistic).
import { RoutingMatrixProvider } from '@/contexts/RoutingMatrixContext';
import PreCallHooksSection from '@/components/ai/PreCallHooksSection';
import { GeminiVoiceAgentModal } from '@/components/ai/GeminiVoiceAgentModal';
import OpenAIVoiceAgentModal from '@/components/ai/OpenAIVoiceAgentModal';
import AIHealthCheck from '@/components/ai/AIHealthCheck';
import RAGAnalyticsDashboard from '@/components/ai/RAGAnalyticsDashboard';
import { AgentBindingsPanel } from '@/components/mcp/AgentBindingsPanel';
import { getErrorMessage, getApiErrorMessage } from '@/lib/utils';
import type { ElevenLabsAgentItem } from '@/components/ai/ElevenLabsAgentConfigModal';
import type { GeminiVoiceAgentItem } from '@/components/ai/GeminiVoiceAgentModal';
import type { AIVoiceAgent as OpenAIVoiceAgentType } from '@/components/ai/OpenAIVoiceAgentModal';
import type { WikiSettings } from '@/components/wiki/types';
import type { PreCallHooksConfig } from '@/components/ai/PreCallHooksSection';

const AnalyticsDashboard = lazy(() => import('@/components/ai/AnalyticsDashboard'));
const RAGAnalytics = lazy(() => import('@/pages/ai/RAGAnalytics'));
const ArchitectModal = lazy(() => import('@/components/ai/architect/ArchitectModal'));
const ProfilingAgentConfigModal = lazy(() => import('@/components/ai/ProfilingAgentConfigModal'));
const BibliotecarioModal = lazy(() => import('@/components/ai/bibliotecario/BibliotecarioModal'));
const A2AGatewayTab = lazy(() => import('@/components/a2a/A2AGatewayTab').then(m => ({ default: m.A2AGatewayTab })));

interface AIProvider {
  id: string;
  name: string;
  provider_type: string;
  api_key_hint: string;
  is_active: boolean;
  is_valid: boolean;
  last_tested_at: string | null;
  openai_project_id?: string;
  routing_mode?: 'legacy_ws_bridge' | 'openai_sip' | 'shadow_both';
  webhook_secret_configured?: boolean;
  webhook_url?: string;
  org_key_configured?: boolean;
  org_api_key_hint?: string;
}

interface RealtimeConfig {
  voice?: string;
  speed?: number;
  voice_instructions?: string;
  stability?: number;
  speaking_rate?: number;
  expressiveness?: string;
  handoff_type?: string;
  handoff_destination?: string;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  language?: string;
  vad_type?: 'server_vad' | 'semantic_vad' | 'none';
  vad_threshold?: number;
  vad_prefix_padding_ms?: number;
  vad_silence_duration_ms?: number;
  vad_eagerness?: 'low' | 'medium' | 'high' | 'auto';
  aec_enabled?: boolean;
}

interface AIVoiceAgent {
  id: string;
  name: string;
  description: string | null;
  model: string;
  temperature: number;
  max_tokens: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  system_prompt: string;
  provider_id: string | null;
  rag_enabled: boolean;
  is_active: boolean;
  conversation_states?: ConversationState[];
  available_tools?: string[];
  handoff_extension?: string;
  language?: string;
  voice_id?: string;
  realtime_config?: RealtimeConfig;
  transcription_enabled?: boolean;
  sentiment_enabled?: boolean;
  context_memory_enabled?: boolean;
  semantic_memory_enabled?: boolean;
  agent_platform?: string;
  template_id?: string | null;
}

interface VoiceAgentForCall {
  id: string;
  name: string;
  description?: string | null;
  is_active?: boolean;
  provider?: string;
  provider_name?: string;
  sip_enabled?: boolean;
}

const AGENT_LANGUAGES = [
  { id: 'it', name: 'Italiano', flag: '🇮🇹' },
  { id: 'en', name: 'English', flag: '🇬🇧' },
  { id: 'fr', name: 'Français', flag: '🇫🇷' },
  { id: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { id: 'es', name: 'Español', flag: '🇪🇸' },
];

interface ConversationState {
  id: string;
  description: string;
  instructions: string[];
  transitions: { next_step: string; condition: string }[];
}


interface AIDocument {
  id: string;
  filename: string;
  chunk_count: number;
  created_at: string;
  status?: string;
}

interface AIURL {
  id: string;
  url: string;
  title: string | null;
  status: string;
  error_message: string | null;
  chunk_count?: number;
  created_at?: string;
}

interface TokenUsage {
  agent_id: string;
  agent_name: string;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  request_count: number;
  estimated_cost: number;
  request_type?: string;
  category?: string;
}

interface RequestLog {
  id: string;
  agent_name: string;
  model: string;
  tokens_used: number;
  total_tokens: number;
  response_time_ms: number;
  latency_ms: number;
  created_at: string;
  status: string;
  request_type?: string;
}

interface CategoryBreakdown {
  category: string;
  request_count: number;
  total_tokens: number;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: string; color: string; bgColor: string }> = {
  voice_call: { label: 'Agenti Vocali', icon: 'phone', color: 'text-blue-500 dark:text-blue-400', bgColor: 'bg-blue-500/20' },
  chat: { label: 'Chatbot', icon: 'message', color: 'text-green-500 dark:text-green-400', bgColor: 'bg-green-500/20' },
  landing_builder: { label: 'Site Builder', icon: 'sparkles', color: 'text-purple-500 dark:text-purple-400', bgColor: 'bg-purple-500/20' },
  landing_image_gen: { label: 'Immagini AI', icon: 'image', color: 'text-pink-500 dark:text-pink-400', bgColor: 'bg-pink-500/20' },
  workflow: { label: 'Workflow', icon: 'git', color: 'text-orange-500 dark:text-orange-400', bgColor: 'bg-orange-500/20' },
  transcription: { label: 'Trascrizioni', icon: 'file', color: 'text-yellow-500 dark:text-yellow-400', bgColor: 'bg-yellow-500/20' },
  sentiment: { label: 'Analisi Sentiment', icon: 'activity', color: 'text-teal-500 dark:text-teal-400', bgColor: 'bg-teal-500/20' },
};

const TTS_VOICES = [
  { id: 'marin', name: 'Marin', description: 'Professionale e naturale (Consigliata)', quality: 'premium' },
  { id: 'cedar', name: 'Cedar', description: 'Calda e professionale', quality: 'premium' },
  { id: 'alloy', name: 'Alloy', description: 'Neutrale e bilanciata', quality: 'standard' },
  { id: 'ash', name: 'Ash', description: 'Calda e naturale', quality: 'standard' },
  { id: 'ballad', name: 'Ballad', description: 'Espressiva e melodica', quality: 'standard' },
  { id: 'coral', name: 'Coral', description: 'Amichevole e chiara', quality: 'standard' },
  { id: 'echo', name: 'Echo', description: 'Profonda e risonante', quality: 'standard' },
  { id: 'fable', name: 'Fable', description: 'Narrativa e coinvolgente', quality: 'standard' },
  { id: 'onyx', name: 'Onyx', description: 'Autoritaria e forte', quality: 'standard' },
  { id: 'nova', name: 'Nova', description: 'Giovane e dinamica', quality: 'standard' },
  { id: 'sage', name: 'Sage', description: 'Saggia e rassicurante', quality: 'standard' },
  { id: 'shimmer', name: 'Shimmer', description: 'Brillante e vivace', quality: 'standard' },
  { id: 'verse', name: 'Verse', description: 'Poetica e fluida', quality: 'standard' }
];

const VOICE_INSTRUCTION_PRESETS = [
  { 
    id: 'assistenza_clienti', 
    label: 'Assistenza Clienti',
    text: 'Parla con tono professionale ma cordiale. Usa un ritmo calmo e rassicurante. Fai pause naturali tra le frasi. Mostra empatia quando il cliente esprime frustrazione. Mantieni un tono positivo e propositivo nella risoluzione dei problemi.'
  },
  { 
    id: 'reception', 
    label: 'Reception / Centralino',
    text: 'Parla con tono accogliente e professionale, come una receptionist esperta. Sii chiara e concisa nelle risposte. Usa un ritmo moderato, non troppo veloce. Mantieni sempre un atteggiamento cortese e disponibile. Saluta sempre con calore.'
  },
  { 
    id: 'vendita', 
    label: 'Vendita / Commerciale',
    text: 'Parla con entusiasmo controllato e professionalità. Usa un tono convincente ma non aggressivo. Fai pause strategiche per dare peso alle informazioni importanti. Sii energico ma naturale. Adatta il ritmo alla conversazione: più lento per spiegazioni tecniche, più dinamico per i vantaggi.'
  },
  { 
    id: 'wake_up', 
    label: 'Wake-up / Promemoria',
    text: 'Parla con tono gentile e allegro, come per dare il buongiorno. Sii breve e diretto nel messaggio. Usa un ritmo rilassato ma chiaro. Concludi sempre con un augurio positivo per la giornata.'
  },
  { 
    id: 'medico', 
    label: 'Studio Medico',
    text: 'Parla con tono calmo, rassicurante e professionale. Usa un ritmo lento e chiaro per garantire la comprensione. Mostra pazienza e empatia. Sii preciso nelle informazioni su appuntamenti e orari. Mantieni sempre un atteggiamento sereno e competente.'
  },
  { 
    id: 'naturale_it', 
    label: 'Naturale Italiano',
    text: 'Parla in modo naturale e fluido in italiano. Usa intonazione variata come in una conversazione reale. Inserisci micro-pause naturali. Evita di suonare robotico o monotono. Adatta il tono emotivo al contesto della conversazione.'
  }
];

const AGENT_TAGS: Record<string, { label: string; color: string }[]> = {
  '1': [{ label: 'Sales', color: 'bg-blue-500/20 text-blue-400 dark:text-blue-300' }],
  '2': [{ label: 'General', color: 'bg-green-500/20 text-green-400 dark:text-green-300' }],
  '3': [{ label: 'Support', color: 'bg-purple-500/20 text-purple-400 dark:text-purple-300' }]
};

type TabType = 'dashboard' | 'providers' | 'agents' | 'agent-bindings' | 'routing' | 'analytics' | 'rag-analytics' | 'transcriptions' | 'health-check' | 'kb-metadata' | 'a2a-gateway';

const AI_MAIN_TABS: readonly HubTab<TabType>[] = [
  { key: 'dashboard',      label: 'Dashboard',      mobileLabel: 'Dash',     icon: Activity },
  { key: 'providers',      label: 'Provider AI',    mobileLabel: 'Provider', icon: Key },
  { key: 'agents',         label: 'Agenti AI',      mobileLabel: 'Agenti',   icon: Bot },
  { key: 'agent-bindings', label: 'Agent Bindings', mobileLabel: 'Bindings', icon: Shield },
  { key: 'routing',        label: 'Routing AI',     mobileLabel: 'Routing',  icon: GitBranch },
  { key: 'analytics',      label: 'Analytics',      mobileLabel: 'Analyt.',  icon: BarChart3 },
  { key: 'rag-analytics',  label: 'RAG Analytics',  mobileLabel: 'RAG',      icon: Database },
  { key: 'transcriptions', label: 'Trascrizioni',   mobileLabel: 'Trascr.',  icon: MessageSquare },
  { key: 'health-check',   label: 'Health Check',   mobileLabel: 'Health',   icon: Shield },
  { key: 'kb-metadata',    label: 'Metadata KB',    mobileLabel: 'Meta',     icon: Tags },
  { key: 'a2a-gateway',    label: 'A2A Gateway',    mobileLabel: 'A2A',      icon: Network },
] as const;
type ProviderSubTab = 'openai' | 'claude' | 'gemini' | 'elevenlabs' | 'custom';
type AgentSubTab = 'voice-openai' | 'voice-elevenlabs' | 'voice-gemini' | 'chatbot' | 'assistants' | 'workflow' | 'system';

interface ChatbotAgent {
  id: string;
  ai_agent_id: string;
  name: string;
  description?: string;
  model: string;
  temperature: number;
  system_prompt: string;
  rag_enabled: boolean;
  context_memory_enabled: boolean;
  semantic_memory_enabled: boolean;
  widget_config: Record<string, unknown>;
  availability_config: Record<string, unknown>;
  click_to_call_enabled: boolean;
  click_to_call_config: Record<string, unknown>;
  lead_capture_enabled: boolean;
  default_stage_id?: string;
  quick_replies: Array<{ text: string; action?: string }>;
  is_active: boolean;
  document_count: number;
  url_count: number;
  chunk_count: number;
  conversation_count: number;
  campaign_names?: string[];
  transcription_enabled?: boolean;
  sentiment_enabled?: boolean;
  created_at: string;
}

interface PersonalAssistantItem {
  id: string;
  ai_agent_id: string;
  name: string;
  description?: string;
  display_name?: string;
  avatar_icon?: string;
  color_theme?: string;
  model: string;
  temperature: number;
  system_prompt: string;
  rag_enabled: boolean;
  provider_id?: string;
  assigned_user_id?: string;
  assigned_user_name?: string;
  tenant_context_enabled: boolean;
  tenant_context_synced_at?: string;
  is_active: boolean;
  document_count: number;
  url_count: number;
  chunk_count: number;
  conversation_count: number;
  transcription_enabled?: boolean;
  sentiment_enabled?: boolean;
  created_at: string;
}

export default function AITools() {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Agent Bindings = concessione tool/workflow ai PA: gestione riservata
  // agli admin (le mutation backend sono admin-gated), tab nascosto agli altri.
  const isAdmin = user?.role === 'admin';
  const visibleTabs = useMemo(
    () => (isAdmin ? AI_MAIN_TABS : AI_MAIN_TABS.filter((t) => t.key !== 'agent-bindings')),
    [isAdmin],
  );
  // Wizard scelta embedder: appare solo all'apertura della Dashboard AI
  // (non più al login). Dismissibile per la sessione via "Più tardi".
  const { needsWizard: gateNeedsWizard, refetch: refetchEmbedderGate } = useEmbedderGate();
  const [embedderWizardDismissed, setEmbedderWizardDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && window.sessionStorage.getItem('embedder-wizard-dismissed') === '1'
  );
  // Listener per il bottone "Configura ora" dell'alert nell'header:
  // resetta il dismiss locale e fa riapparire il wizard.
  useEffect(() => {
    const handler = () => setEmbedderWizardDismissed(false);
    window.addEventListener('embedder-wizard:reopen', handler);
    return () => window.removeEventListener('embedder-wizard:reopen', handler);
  }, []);
  const needsEmbedderWizard = gateNeedsWizard && !embedderWizardDismissed;
  
  const [activeTab, setActiveTabRaw] = useState<TabType>('dashboard');
  const [analyticsTabVisited, setAnalyticsTabVisited] = useState(false);
  const setActiveTab = useCallback((tab: TabType) => {
    if (tab === 'analytics') setAnalyticsTabVisited(true);
    setActiveTabRaw(tab);
  }, []);

  const focusServiceRow = useCallback((focusService: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-service-row="${focusService}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-400');
        setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 2000);
      }
    }, 250);
  }, []);

  const isTabType = useCallback((value: string | null | undefined): value is TabType => {
    return !!value && AI_MAIN_TABS.some(t => t.key === value);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { tab?: string; focusService?: string } | undefined;
      const tab: TabType = isTabType(detail?.tab) ? detail.tab : 'routing';
      setActiveTab(tab);
      if (detail?.focusService) focusServiceRow(detail.focusService);
    };
    window.addEventListener('ai-tools:focus-service', handler);
    return () => window.removeEventListener('ai-tools:focus-service', handler);
  }, [focusServiceRow, isTabType, setActiveTab]);

  // Deep-link contract: ?focus=<service_key> (and optional &tab=<tab>) opens
  // Service Routing (or the requested tab) and scrolls to / highlights the row.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const focus = params.get('focus');
      if (!focus) return;
      const rawTab = params.get('tab');
      const tabParam: TabType = isTabType(rawTab) ? rawTab : 'routing';
      setActiveTab(tabParam);
      focusServiceRow(focus);
    } catch {
      /* ignore */
    }
  }, [focusServiceRow, isTabType, setActiveTab]);
  const [providerSubTab, setProviderSubTab] = useState<ProviderSubTab>('openai');
  const [agentSubTab, setAgentSubTab] = useState<AgentSubTab>('voice-openai');
  const [analyticsProvider, setAnalyticsProvider] = useState<'all' | 'openai' | 'elevenlabs'>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [agents, setAgents] = useState<AIVoiceAgent[]>([]);
  const [voiceAgentsForCall, setVoiceAgentsForCall] = useState<VoiceAgentForCall[]>([]);
  const openaiRealtimeCatalogQuery = useChatModelOptions('openai-realtime');
  const openaiRealtimeModelsOffline =
    openaiRealtimeCatalogQuery.data?.source === 'fallback' || openaiRealtimeCatalogQuery.isError;
  const [chatbotAgents, setChatbotAgents] = useState<ChatbotAgent[]>([]);
  const [personalAssistants, setPersonalAssistants] = useState<PersonalAssistantItem[]>([]);
  const [aiEnabled, setAiEnabled] = useState(true);
  
  const [newApiKey, setNewApiKey] = useState('');
  const [openaiProjectIdInput, setOpenaiProjectIdInput] = useState('');
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  
  const [selectedAgent, setSelectedAgent] = useState<AIVoiceAgent | null>(null);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [openaiAgentDeleteConfirm, setOpenaiAgentDeleteConfirm] = useState<AIVoiceAgent | null>(null);
  const [architectModalOpen, setArchitectModalOpen] = useState(false);
  const [profilingModalOpen, setProfilingModalOpen] = useState(false);
  // Task #3414 — Commissioning Agent AI (hub Incentivazione).
  const [commissioningModalOpen, setCommissioningModalOpen] = useState(false);
  const [isDeletingAgent, setIsDeletingAgent] = useState(false);
  const [showWorkflowBuilderModal, setShowWorkflowBuilderModal] = useState(false);
  const [showChatbotModal, setShowChatbotModal] = useState(false);
  const [selectedChatbot, setSelectedChatbot] = useState<ChatbotAgent | null>(null);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedChatbot, setEmbedChatbot] = useState<ChatbotAgent | null>(null);
  const [showAssistantModal, setShowAssistantModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<PersonalAssistantItem | null>(null);
  const [elevenLabsAgents, setElevenLabsAgents] = useState<ElevenLabsAgentItem[]>([]);
  const [showElevenLabsModal, setShowElevenLabsModal] = useState(false);
  const [selectedElevenLabsAgent, setSelectedElevenLabsAgent] = useState<ElevenLabsAgentItem | null>(null);
  const [geminiVoiceAgents, setGeminiVoiceAgents] = useState<GeminiVoiceAgentItem[]>([]);
  const [showGeminiVoiceModal, setShowGeminiVoiceModal] = useState(false);
  const [selectedGeminiVoiceAgent, setSelectedGeminiVoiceAgent] = useState<GeminiVoiceAgentItem | null>(null);
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [editingAgent, setEditingAgent] = useState<Partial<AIVoiceAgent>>({});
  const [agentDocuments, setAgentDocuments] = useState<AIDocument[]>([]);
  const [agentUrls, setAgentUrls] = useState<AIURL[]>([]);
  const [newDocContent, setNewDocContent] = useState('');
  const [newDocName, setNewDocName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [chunkPreview, setChunkPreview] = useState<{
    filename: string;
    original_length: number;
    original_tokens: number;
    chunk_count: number;
    total_tokens: number;
    avg_tokens_per_chunk: number;
    chunks: Array<{ index: number; content: string; token_count: number; char_count: number; preview: string }>;
  } | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [urlPreview, setUrlPreview] = useState<{
    url: string;
    title: string;
    auto_name: string;
    raw_content?: string;
    original_length: number;
    original_tokens: number;
    chunk_count: number;
    total_tokens: number;
    avg_tokens_per_chunk: number;
    chunks: Array<{ index: number; content: string; token_count: number; char_count: number; preview: string }>;
  } | null>(null);
  const [isUrlPreviewLoading, setIsUrlPreviewLoading] = useState(false);
  const [firecrawlActive, setFirecrawlActive] = useState<boolean | null>(null);
  const [crawlMode, setCrawlMode] = useState<'single' | 'site'>('single');
  const [crawlMaxPages, setCrawlMaxPages] = useState(10);
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState<{ status: string; pagesFound: number; pagesProcessed: number; totalChunks: number } | null>(null);

  const [tokenUsage, setTokenUsage] = useState<TokenUsage[]>([]);
  const [requestLogs, setRequestLogs] = useState<RequestLog[]>([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<CategoryBreakdown[]>([]);
  const [analyticsFilter, setAnalyticsFilter] = useState<string>('all');
  const [elevenLabsAnalytics, setElevenLabsAnalytics] = useState<{
    stats: { total_conversations: number; total_duration: number; avg_duration: number; completed: number; failed: number };
    recent: Array<{ id: string; agent_name: string; status: string; duration_seconds: number; channel: string; detected_language: string; created_at: string }>;
    daily: Array<{ date: string; count: number; total_duration: number }>;
  } | null>(null);
  
  const [providerStatus, setProviderStatus] = useState<{
    openai?: { active: boolean };
    claude?: { active: boolean; configured: boolean; connection_valid?: boolean };
    google?: { active: boolean; configured: boolean; connection_valid?: boolean };
    elevenlabs?: { active: boolean; configured: boolean; connection_valid?: boolean };
    services?: Record<string, string>;
  } | null>(null);
  
  // File upload ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Voice settings state with new naturalness controls
  const [voiceSettings, setVoiceSettings] = useState({
    voice: 'marin',
    speed: 1.0,
    voice_instructions: '',
    stability: 0.5,
    speaking_rate: 0.9,
    expressiveness: 'conversational',
    handoff_type: '',
    handoff_destination: '',
    vad_type: 'server_vad' as 'server_vad' | 'semantic_vad' | 'none',
    vad_threshold: 0.5,
    vad_prefix_padding_ms: 300,
    vad_silence_duration_ms: 500,
    vad_eagerness: 'auto' as 'low' | 'medium' | 'high' | 'auto',
    aec_enabled: false
  });
  const [isTestingVoice, setIsTestingVoice] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  
  // Progress state for uploads
  const [uploadProgress, setUploadProgress] = useState<{
    stage: 'idle' | 'uploading' | 'chunking' | 'embedding' | 'completed';
    progress: number;
    message: string;
  }>({ stage: 'idle', progress: 0, message: '' });
  
  // RAG Analytics modal state
  const [ragAnalyticsAgentId, setRagAnalyticsAgentId] = useState<string | null>(null);
  const [showOrgKeySection, setShowOrgKeySection] = useState(false);
  const [orgApiKey, setOrgApiKey] = useState('');
  const [savingOrgKey, setSavingOrgKey] = useState(false);
  const [extensions, setExtensions] = useState<{id: string; extension_number: string; display_name: string}[]>([]);
  const [ringGroups, setRingGroups] = useState<{id: string; name: string; extension: string}[]>([]);
  const [queues, setQueues] = useState<{id: string; name: string; extension: string}[]>([]);
  const [trunks, setTrunks] = useState<{id: string; name: string; gateway_name?: string}[]>([]);
  const [chatbotDeleteConfirm, setChatbotDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [isChatbotDeleting, setIsChatbotDeleting] = useState(false);

  // Bibliotecario (Company Wiki) — system agent surfaced here for LLM/settings management.
  const [bibliotecarioSettings, setBibliotecarioSettings] = useState<WikiSettings | null>(null);
  const [bibliotecarioModalOpen, setBibliotecarioModalOpen] = useState(false);
  const [bibliotecarioWikiPath] = useState<string>(() => {
    try {
      const seg = window.location.pathname.split('/').filter(Boolean)[0];
      return seg ? `/${seg}/company-wiki` : '/company-wiki';
    } catch { return '/company-wiki'; }
  });
  useEffect(() => {
    apiClient.get<{ settings: WikiSettings; prompt: string }>('/company-wiki/settings')
      .then((r) => { if (r.data?.settings) setBibliotecarioSettings(r.data.settings); })
      .catch(() => { /* admin-only endpoint; ignore for non-admins */ });
  }, []);

  // Estrae un array da un payload dinamico che può esporre la lista sotto
  // varie chiavi (top-level, items, <key>, data.*). Volutamente permissivo.
  const getList = (res: unknown, key: string): unknown[] => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    const r = res as Record<string, unknown>;
    if (Array.isArray(r.items)) return r.items;
    if (Array.isArray(r[key])) return r[key] as unknown[];
    if (Array.isArray(r.data)) return r.data;
    const rd = r.data as Record<string, unknown> | undefined;
    if (rd) {
      if (Array.isArray(rd.items)) return rd.items;
      if (Array.isArray(rd[key])) return rd[key] as unknown[];
      if (Array.isArray(rd.data)) return rd.data;
      const rdd = rd.data as Record<string, unknown> | undefined;
      if (rdd && Array.isArray(rdd.items)) return rdd.items;
    }
    return [];
  };

  const { data: _primaryData } = useQuery({
    queryKey: ['ai-tools-primary'],
    queryFn: async () => {
      const [providersRes, agentsRes, allVoiceAgentsRes, chatbotRes, paRes] = await Promise.allSettled([
        apiClient.get<AIProvider[]>('/ai/providers'),
        apiClient.get<AIVoiceAgent[]>('/ai/voice-agents?platform=openai'),
        apiClient.get<VoiceAgentForCall[]>('/ai/voice-agents'),
        apiClient.get<ChatbotAgent[]>('/chatbot/agents'),
        apiClient.get<PersonalAssistantItem[]>('/personal-assistant/agents'),
      ]);
      return {
        providers: providersRes.status === 'fulfilled' ? (providersRes.value.data || []) : [],
        agents: agentsRes.status === 'fulfilled'
          ? (agentsRes.value.data || [])
          : [],
        voiceAgentsForCall: (allVoiceAgentsRes.status === 'fulfilled'
          ? (allVoiceAgentsRes.value.data || [])
          : []) as VoiceAgentForCall[],
        chatbotAgents: chatbotRes.status === 'fulfilled' ? (chatbotRes.value.data || []) : [],
        personalAssistants: paRes.status === 'fulfilled' ? (paRes.value.data || []) : [],
      };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (_primaryData) {
      setProviders(_primaryData.providers);
      setAgents(_primaryData.agents);
      setVoiceAgentsForCall(_primaryData.voiceAgentsForCall);
      setChatbotAgents(_primaryData.chatbotAgents);
      setPersonalAssistants(_primaryData.personalAssistants);
      setIsLoading(false);
    }
  }, [_primaryData]);

  const { data: _secondaryData } = useQuery({
    queryKey: ['ai-tools-secondary'],
    queryFn: async () => {
      const [elRes, extRes, rgRes, qRes, trunkRes, campRes, provStatusRes, gvRes] = await Promise.allSettled([
        apiClient.get('/elevenlabs/agents'),
        apiClient.get('/voip/sip-extensions'),
        apiClient.get('/voip/ring-groups'),
        apiClient.get('/voip/queues'),
        apiClient.get('/sip-trunks'),
        apiClient.get('/crm/campaigns'),
        apiClient.get('/ai-tools/provider-status'),
        apiClient.get('/gemini-voice/agents'),
      ]);

      // Payload dinamici: le rotte legacy possono annidare i dati sotto
      // .data / .data.data. Si legge in modo difensivo con accessi tipizzati.
      type Nested = { data?: unknown; campaigns?: unknown } & Record<string, unknown>;
      const asNested = (v: unknown): Nested | null =>
        v && typeof v === 'object' ? (v as Nested) : null;

      const elData = elRes.status === 'fulfilled' ? asNested(elRes.value.data) : null;
      const gvData = gvRes.status === 'fulfilled' ? asNested(gvRes.value.data) : null;
      const extData = extRes.status === 'fulfilled' ? getList(extRes.value.data, 'extensions') : [];
      const rgData = rgRes.status === 'fulfilled' ? getList(rgRes.value.data, 'ring_groups') : [];
      const qData = qRes.status === 'fulfilled' ? getList(qRes.value.data, 'queues') : [];
      const trunkData = trunkRes.status === 'fulfilled' ? getList(trunkRes.value.data, 'trunks') : [];
      const campData = campRes.status === 'fulfilled' ? campRes.value.data : null;
      type ProviderStatus = {
        openai?: { active: boolean };
        claude?: { active: boolean; configured: boolean; connection_valid?: boolean };
        google?: { active: boolean; configured: boolean; connection_valid?: boolean };
        elevenlabs?: { active: boolean; configured: boolean; connection_valid?: boolean };
        services?: Record<string, string>;
      };
      const psData = provStatusRes.status === 'fulfilled'
        ? ((asNested(provStatusRes.value.data)?.data ?? provStatusRes.value.data) as ProviderStatus | null)
        : null;

      const asRec = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
      const str = (v: unknown): string => (typeof v === 'string' ? v : '');

      return {
        elevenLabsAgents: ((elData?.data ?? elData) || []) as ElevenLabsAgentItem[],
        geminiVoiceAgents: ((gvData?.data ?? gvData) || []) as GeminiVoiceAgentItem[],
        extensions: extData.map((raw) => {
          const e = asRec(raw);
          return {
            id: e.id as string,
            extension_number: (e.extension || e.extension_number) as string,
            display_name: str(e.display_name || e.caller_id_name || e.name || e.extension),
          };
        }),
        ringGroups: rgData.map((raw) => {
          const r = asRec(raw);
          return {
            id: r.id as string, name: r.name as string,
            extension: str(r.extension || r.ring_group_extension),
          };
        }),
        queues: qData.map((raw) => {
          const q = asRec(raw);
          return { id: q.id as string, name: q.name as string, extension: str(q.extension) };
        }),
        trunks: trunkData
          .filter((raw) => asRec(raw).enabled !== false)
          .map((raw) => { const t = asRec(raw); return { id: t.id as string, name: t.name as string }; }),
        campaigns: (() => {
          const list = Array.isArray(campData) ? campData : (asNested(campData)?.campaigns as unknown[] | undefined) || [];
          return list
            .filter((raw) => asRec(raw).is_active !== false)
            .map((raw) => { const c = asRec(raw); return { id: c.id as string, name: c.name as string }; });
        })(),
        providerStatus: psData || null,
      };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !!_primaryData,
  });

  useEffect(() => {
    if (_secondaryData) {
      setElevenLabsAgents(_secondaryData.elevenLabsAgents);
      setGeminiVoiceAgents(_secondaryData.geminiVoiceAgents);
      setExtensions(_secondaryData.extensions);
      setRingGroups(_secondaryData.ringGroups);
      setQueues(_secondaryData.queues);
      setTrunks(_secondaryData.trunks);
      setCampaigns(_secondaryData.campaigns);
      setProviderStatus(_secondaryData.providerStatus);
    }
  }, [_secondaryData]);

  const loadData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['ai-tools-primary'] });
    queryClient.invalidateQueries({ queryKey: ['ai-tools-secondary'] });
  }, [queryClient]);

  const loadAnalytics = useCallback(async (provider?: string) => {
    try {
      if (provider === 'elevenlabs') {
        const elRes = await apiClient.get<{
          stats: { total_conversations: number; total_duration: number; avg_duration: number; completed: number; failed: number };
          recent: Array<{ id: string; agent_name: string; status: string; duration_seconds: number; channel: string; detected_language: string; created_at: string }>;
          daily: Array<{ date: string; count: number; total_duration: number }>;
        }>('/elevenlabs/analytics');
        setElevenLabsAnalytics(elRes.data || null);
        setTokenUsage([]);
        setRequestLogs([]);
        setCategoryBreakdown([]);
      } else {
        const providerParam = provider && provider !== 'all' ? `?provider=${provider}` : '';
        // Envelope eterogeneo: le rotte analytics possono esporre campi extra
        // fuori dall'ApiResponse standard (es. `categories`). Tipo permissivo.
        type AnalyticsRes = {
          data?: unknown;
          categories?: CategoryBreakdown[];
        } | null;
        const fetches: Promise<AnalyticsRes>[] = [
          apiClient.get(`/ai/analytics/usage${providerParam}`) as Promise<AnalyticsRes>,
          apiClient.get<RequestLog[]>('/ai/analytics/logs') as Promise<AnalyticsRes>,
        ];
        if (!provider || provider === 'all') {
          fetches.push(apiClient.get('/elevenlabs/analytics').catch(() => null) as Promise<AnalyticsRes>);
        }
        const results = await Promise.all(fetches);
        setTokenUsage((results[0]?.data as TokenUsage[]) || []);
        setCategoryBreakdown(results[0]?.categories || []);
        setRequestLogs((results[1]?.data as RequestLog[]) || []);
        if (results[2]) {
          setElevenLabsAnalytics((results[2]?.data as typeof elevenLabsAnalytics) || null);
        } else {
          setElevenLabsAnalytics(null);
        }
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      setTokenUsage([]);
      setRequestLogs([]);
      setCategoryBreakdown([]);
      setElevenLabsAnalytics(null);
    }
  }, []);

  useEffect(() => {
    if ((activeTab as string) === '_analytics_old_disabled') {
      void loadAnalytics(analyticsProvider);
    }
  }, [activeTab, analyticsProvider, loadAnalytics]);

  const createProvider = async () => {
    if (!newApiKey) {
      toast({ title: 'Errore', description: 'Inserisci la API key', variant: 'destructive' });
      return;
    }
    
    try {
      setIsProcessing(true);
      await apiClient.post('/ai/providers', { name: 'OpenAI', api_key: newApiKey });
      toast({ title: 'Successo', description: 'Provider configurato' });
      setNewApiKey('');
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const testProvider = async (id: string) => {
    setTestingProvider(id);
    try {
      const res = await apiClient.post<{ success: boolean; error?: string }>(`/ai/providers/${id}/test`);
      if (res.data?.success) {
        toast({ title: 'Connessione OK', description: 'API key valida' });
      } else {
        toast({ title: 'Errore', description: res.data?.error || res.error, variant: 'destructive' });
      }
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTestingProvider(null);
    }
  };

  const toggleProvider = async (id: string, is_active: boolean) => {
    try {
      await apiClient.patch(`/ai/providers/${id}/status`, { is_active });
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    }
  };

  const deleteProvider = async (id: string) => {
    if (!confirm('Eliminare questo provider?')) return;
    try {
      await apiClient.delete(`/ai/providers/${id}`);
      toast({ title: 'Provider eliminato' });
      loadData();
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    }
  };

  const openAgentConfig = (agent: AIVoiceAgent) => {
    setSelectedAgent(agent);
    setShowAgentDialog(true);
  };

  const toggleAgent = async (id: string, is_active: boolean) => {
    try {
      setAgents(prev => prev.map(a => a.id === id ? { ...a, is_active } : a));
      await apiClient.put(`/ai/voice-agents/${id}`, { is_active });
    } catch (error) {
      setAgents(prev => prev.map(a => a.id === id ? { ...a, is_active: !is_active } : a));
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    }
  };

  const toggleChatbot = async (id: string, is_active: boolean) => {
    try {
      setChatbotAgents(prev => prev.map(a => a.id === id ? { ...a, is_active } : a));
      await apiClient.put(`/chatbot/agents/${id}`, { is_active });
    } catch (error) {
      setChatbotAgents(prev => prev.map(a => a.id === id ? { ...a, is_active: !is_active } : a));
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const toggleAssistant = async (id: string, is_active: boolean) => {
    try {
      setPersonalAssistants(prev => prev.map(a => a.id === id ? { ...a, is_active } : a));
      await apiClient.put(`/personal-assistant/agents/${id}`, { is_active });
    } catch (error) {
      setPersonalAssistants(prev => prev.map(a => a.id === id ? { ...a, is_active: !is_active } : a));
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const toggleElevenLabsAgent = async (id: string, is_active: boolean) => {
    try {
      setElevenLabsAgents(prev => prev.map(a => a.id === id ? { ...a, is_active } : a));
      await apiClient.patch(`/elevenlabs/agents/${id}`, { is_active });
    } catch (error) {
      setElevenLabsAgents(prev => prev.map(a => a.id === id ? { ...a, is_active: !is_active } : a));
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const addDocument = async () => {
    if (!selectedAgent || !newDocContent || !newDocName) {
      toast({ title: 'Errore', description: 'Inserisci nome e contenuto', variant: 'destructive' });
      return;
    }

    const placeholders = ['[PDF - in elaborazione...]', '[DOCX - in elaborazione...]', '[PDF - processing...]', '[DOCX - processing...]'];
    if (placeholders.includes(newDocContent.trim())) {
      toast({ title: 'Documento non pronto', description: 'Il testo del documento non è stato ancora estratto. Ricarica il file.', variant: 'destructive' });
      return;
    }

    try {
      setIsProcessing(true);
      await apiClient.post(`/knowledge-base/${selectedAgent.id}/documents`, {
        filename: newDocName,
        content: newDocContent
      });
      toast({ title: 'Documento aggiunto', description: 'Chunking e embedding completati' });
      setNewDocName('');
      setNewDocContent('');
      
      const res = await apiClient.get<AIDocument[]>(`/knowledge-base/${selectedAgent.id}/documents`);
      setAgentDocuments(res.data || []);
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteDocument = async (docId: string) => {
    if (!selectedAgent) return;
    try {
      await apiClient.delete(`/knowledge-base/${selectedAgent.id}/documents/${docId}`);
      setAgentDocuments(docs => docs.filter(d => d.id !== docId));
      toast({ title: 'Documento eliminato' });
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    }
  };

  const addUrl = async () => {
    if (!selectedAgent || !newUrl) {
      toast({ title: 'Errore', description: 'Inserisci URL', variant: 'destructive' });
      return;
    }

    try {
      setIsProcessing(true);
      await apiClient.post(`/knowledge-base/${selectedAgent.id}/urls`, { url: newUrl });
      toast({ title: 'URL aggiunto', description: 'Scraping e embedding in corso...' });
      setNewUrl('');
      
      const res = await apiClient.get<AIURL[]>(`/knowledge-base/${selectedAgent.id}/urls`);
      setAgentUrls(res.data || []);
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteUrl = async (urlId: string) => {
    if (!selectedAgent) return;
    try {
      await apiClient.delete(`/knowledge-base/${selectedAgent.id}/urls/${urlId}`);
      setAgentUrls(urls => urls.filter(u => u.id !== urlId));
      toast({ title: 'URL eliminato' });
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    }
  };
  
  // File picker handler
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    const allowedTypes = ['text/plain', 'text/markdown', 'application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    const allowedExtensions = ['.txt', '.md', '.pdf', '.csv', '.doc', '.docx'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast({ 
        title: 'Tipo file non supportato', 
        description: 'Supportati: TXT, MD, CSV, PDF, DOCX', 
        variant: 'destructive' 
      });
      return;
    }
    
    try {
      setUploadProgress({ stage: 'uploading', progress: 20, message: 'Caricamento file...' });
      
      const isPdf = fileExt === '.pdf' || file.type === 'application/pdf';
      const isDocx = fileExt === '.docx' || fileExt === '.doc' || file.type.includes('word');
      const isBinaryFile = isPdf || isDocx;
      
      let content: string;
      let fileBase64: string | undefined;
      
      if (isBinaryFile) {
        // Per PDF/DOCX: invia come base64 al backend per parsing
        // Usa chunks per evitare "Maximum call stack exceeded" con file grandi
        const arrayBuffer = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        const chunkSize = 8192;
        let binaryString = '';
        for (let i = 0; i < bytes.length; i += chunkSize) {
          const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
          binaryString += String.fromCharCode.apply(null, Array.from(chunk));
        }
        fileBase64 = btoa(binaryString);
        content = isPdf ? '[PDF - in elaborazione...]' : '[DOCX - in elaborazione...]';
      } else {
        // Per file di testo: leggi come testo
        content = await file.text();
      }
      
      setNewDocName(file.name);
      setNewDocContent(content);
      
      setUploadProgress({ stage: 'chunking', progress: 50, message: isBinaryFile ? 'Estrazione testo dal documento...' : 'Analisi contenuto...' });
      
      if (selectedAgent) {
        try {
          setIsPreviewLoading(true);
          const previewRes = await apiClient.post(`/knowledge-base/${selectedAgent.id}/documents/preview`, {
            content: isBinaryFile ? undefined : content,
            fileBase64: isBinaryFile ? fileBase64 : undefined,
            filename: file.name
          });
          // apiClient returns unwrapped JSON directly
          const payload = previewRes as unknown as { success: boolean; data: typeof chunkPreview & { extracted_text?: string } };
          if (payload?.success && payload?.data) {
            setChunkPreview(payload.data);
            if (isBinaryFile && payload.data.extracted_text) {
              content = payload.data.extracted_text;
              setNewDocContent(content);
            }
          }
        } catch (e) {
          console.warn('Preview failed:', e);
          toast({ title: 'Errore preview', description: getErrorMessage(e) || 'Errore durante analisi', variant: 'destructive' });
          if (isBinaryFile) {
            setNewDocContent('');
            setNewDocName('');
            setUploadProgress({ stage: 'idle', progress: 0, message: '' });
            return;
          }
        } finally {
          setIsPreviewLoading(false);
        }
      }
      
      if (isBinaryFile && (content === '[PDF - in elaborazione...]' || content === '[DOCX - in elaborazione...]')) {
        toast({ title: 'Errore', description: 'Impossibile estrarre il testo dal documento. Riprova o usa un file diverso.', variant: 'destructive' });
        setNewDocContent('');
        setNewDocName('');
        setUploadProgress({ stage: 'idle', progress: 0, message: '' });
        return;
      }

      setUploadProgress({ stage: 'completed', progress: 100, message: 'Anteprima pronta! Rivedi i chunks e clicca "Aggiungi e Indicizza"' });
      
      setTimeout(() => {
        setUploadProgress({ stage: 'idle', progress: 0, message: '' });
      }, 5000);
      
    } catch (error) {
      toast({ title: 'Errore lettura file', description: getErrorMessage(error), variant: 'destructive' });
      setUploadProgress({ stage: 'idle', progress: 0, message: '' });
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Document upload with progress
  const addDocumentWithProgress = async () => {
    if (!selectedAgent || !newDocContent || !newDocName) {
      toast({ title: 'Errore', description: 'Inserisci nome e contenuto', variant: 'destructive' });
      return;
    }

    const placeholders = ['[PDF - in elaborazione...]', '[DOCX - in elaborazione...]', '[PDF - processing...]', '[DOCX - processing...]'];
    if (placeholders.includes(newDocContent.trim())) {
      toast({ title: 'Documento non pronto', description: 'Il testo del documento non è stato ancora estratto. Ricarica il file.', variant: 'destructive' });
      return;
    }

    try {
      setIsProcessing(true);
      setUploadProgress({ stage: 'uploading', progress: 10, message: 'Invio documento...' });
      
      // Simulate stages for better UX (backend does all at once)
      setTimeout(() => setUploadProgress({ stage: 'chunking', progress: 40, message: 'Divisione in chunks...' }), 500);
      setTimeout(() => setUploadProgress({ stage: 'embedding', progress: 70, message: 'Generazione embeddings...' }), 1500);
      
      await apiClient.post(`/knowledge-base/${selectedAgent.id}/documents`, {
        filename: newDocName,
        content: newDocContent
      });
      
      setUploadProgress({ stage: 'completed', progress: 100, message: 'Completato!' });
      toast({ title: 'Documento aggiunto', description: 'Chunking e embedding completati' });
      setNewDocName('');
      setNewDocContent('');
      setChunkPreview(null);
      
      const res = await apiClient.get<AIDocument[]>(`/knowledge-base/${selectedAgent.id}/documents`);
      setAgentDocuments(res.data || []);
      
      setTimeout(() => setUploadProgress({ stage: 'idle', progress: 0, message: '' }), 2000);
    } catch (error) {
      setUploadProgress({ stage: 'idle', progress: 0, message: '' });
      toast({ title: 'Errore', description: getApiErrorMessage(error), variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };
  
  // URL preview - fetch and show chunks before indexing
  const previewUrl = async () => {
    if (!selectedAgent || !newUrl) {
      toast({ title: 'Errore', description: 'Inserisci URL', variant: 'destructive' });
      return;
    }

    try {
      setIsUrlPreviewLoading(true);
      setUrlPreview(null);
      
      // apiClient.post returns the parsed JSON directly (not wrapped in .data)
      // Backend sends: { success: true, data: { chunks, totalTokens, ... } }
      const response = await apiClient.post<{ success: boolean; data: typeof urlPreview }>(
        `/knowledge-base/${selectedAgent.id}/urls/preview`,
        { url: newUrl }
      );
      
      // response is directly { success, data } since apiClient unwraps fetch response
      const payload = response as unknown as { success: boolean; data: typeof urlPreview };
      
      if (payload?.success && payload?.data) {
        setUrlPreview(payload.data);
      } else {
        toast({ title: 'Errore', description: 'Risposta non valida dal server', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Impossibile analizzare URL', variant: 'destructive' });
    } finally {
      setIsUrlPreviewLoading(false);
    }
  };
  
  // URL add with progress
  const addUrlWithProgress = async () => {
    if (!selectedAgent || !newUrl) {
      toast({ title: 'Errore', description: 'Inserisci URL', variant: 'destructive' });
      return;
    }

    try {
      setIsProcessing(true);
      setUploadProgress({ stage: 'uploading', progress: 10, message: 'Invio URL al server...' });
      
      // Submit the URL
      const submitRes = await apiClient.post<{ id?: string }>(`/knowledge-base/${selectedAgent.id}/urls`, { url: newUrl });
      // Il server può restituire l'oggetto sotto `data` oppure direttamente.
      const urlData = (submitRes?.data ?? (submitRes as unknown as { id?: string }));
      const newUrlId = urlData?.id;
      
      setNewUrl('');
      setUrlPreview(null);
      
      // Polling to check processing status (background processing)
      setUploadProgress({ stage: 'chunking', progress: 30, message: 'Scraping pagina...' });
      
      // Poll every 2 seconds, max 30 seconds
      let attempts = 0;
      const maxAttempts = 15;
      
      const pollStatus = async (): Promise<void> => {
        attempts++;
        const res = await apiClient.get<{ id: string; url: string; title: string | null; status: string; error_message: string | null; chunk_count?: number; created_at?: string }[]>(`/knowledge-base/${selectedAgent.id}/urls`);
        const urls = (res?.data || (res as unknown as AIURL[])) as AIURL[];
        setAgentUrls(urls);
        
        const targetUrl = urls.find(u => u.id === newUrlId);
        
        if (targetUrl?.status === 'completed') {
          setUploadProgress({ stage: 'completed', progress: 100, message: `Completato! ${targetUrl.chunk_count || 0} chunks creati` });
          toast({ title: 'URL indicizzato', description: `${targetUrl.chunk_count || 0} chunks creati` });
          setTimeout(() => setUploadProgress({ stage: 'idle', progress: 0, message: '' }), 3000);
          return;
        } else if (targetUrl?.status === 'error') {
          setUploadProgress({ stage: 'idle', progress: 0, message: '' });
          toast({ title: 'Errore indicizzazione', description: targetUrl.error_message || 'Errore sconosciuto', variant: 'destructive' });
          return;
        } else if (attempts >= maxAttempts) {
          setUploadProgress({ stage: 'completed', progress: 100, message: 'Elaborazione in corso in background...' });
          toast({ title: 'Elaborazione in corso', description: 'L\'URL verrà indicizzato a breve' });
          setTimeout(() => setUploadProgress({ stage: 'idle', progress: 0, message: '' }), 2000);
          return;
        }
        
        // Update progress message
        const progressPercent = 30 + (attempts / maxAttempts) * 60;
        const messages = ['Scraping pagina...', 'Estrazione testo...', 'Creazione! chunks...', 'Generazione embeddings...'];
        const msgIndex = Math.min(Math.floor(attempts / 4), messages.length - 1);
        setUploadProgress({ stage: 'embedding', progress: progressPercent, message: messages[msgIndex]! });
        
        // Wait 2 seconds and poll again
        await new Promise(resolve => setTimeout(resolve, 2000));
        return pollStatus();
      };
      
      await pollStatus();
      
    } catch (error) {
      setUploadProgress({ stage: 'idle', progress: 0, message: '' });
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Errore durante indicizzazione', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const crawlSiteWithProgress = async () => {
    if (!selectedAgent || !newUrl) {
      toast({ title: 'Errore', description: 'Inserisci URL', variant: 'destructive' });
      return;
    }

    try {
      setIsCrawling(true);
      setCrawlProgress({ status: 'crawling', pagesFound: 0, pagesProcessed: 0, totalChunks: 0 });

      const submitRes = await apiClient.post<{ urlId?: string }>(`/knowledge-base/${selectedAgent.id}/urls/crawl`, {
        url: newUrl,
        maxPages: crawlMaxPages
      });
      const crawlData = (submitRes?.data ?? (submitRes as unknown as { urlId?: string }));
      const urlId = crawlData?.urlId;

      if (!urlId) {
        throw new Error('Server non ha restituito un ID valido');
      }

      setNewUrl('');
      setUrlPreview(null);

      let attempts = 0;
      const maxAttempts = 60;

      const pollCrawlStatus = async (): Promise<void> => {
        attempts++;
        try {
          const statusRes = await apiClient.get<{
            status: string; pagesFound?: number; pagesProcessed?: number; totalChunks?: number; error?: string;
          }>(`/knowledge-base/${selectedAgent.id}/urls/crawl/${urlId}/status`);
          const statusData = (statusRes?.data ?? (statusRes as unknown as {
            status: string; pagesFound?: number; pagesProcessed?: number; totalChunks?: number; error?: string;
          }));

          setCrawlProgress({
            status: statusData.status,
            pagesFound: statusData.pagesFound || 0,
            pagesProcessed: statusData.pagesProcessed || 0,
            totalChunks: statusData.totalChunks || 0
          });

          if (statusData.status === 'completed') {
            toast({ title: 'Crawl completato', description: `${statusData.pagesProcessed || 0} pagine, ${statusData.totalChunks || 0} chunks creati` });
            const urlsRes = await apiClient.get<{ id: string; url: string; title: string | null; status: string; error_message: string | null; chunk_count?: number; created_at?: string }[]>(`/knowledge-base/${selectedAgent.id}/urls`);
            setAgentUrls((urlsRes?.data || (urlsRes as unknown as AIURL[])) as AIURL[]);
            setTimeout(() => setCrawlProgress(null), 5000);
            return;
          }

          if (statusData.status === 'error') {
            toast({ title: 'Errore crawl', description: statusData.error || 'Errore sconosciuto', variant: 'destructive' });
            setCrawlProgress(null);
            return;
          }

          if (attempts >= maxAttempts) {
            toast({ title: 'Crawl in corso', description: 'Il crawl continua in background. Ricarica la pagina tra qualche minuto.' });
            setTimeout(() => setCrawlProgress(null), 3000);
            return;
          }

          await new Promise(resolve => setTimeout(resolve, 3000));
          return pollCrawlStatus();
        } catch (pollErr) {
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 3000));
            return pollCrawlStatus();
          }
          throw pollErr;
        }
      };

      await pollCrawlStatus();
    } catch (error) {
      setCrawlProgress(null);
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Errore durante il crawl del sito', variant: 'destructive' });
    } finally {
      setIsCrawling(false);
    }
  };

  // Test voice
  const testVoice = async () => {
    if (!selectedAgent) return;
    
    try {
      setIsTestingVoice(true);
      setAudioUrl(null);
      
      const response = await apiClient.post<{ data: { audioUrl: string } }>(`/ai/voice-agents/${selectedAgent.id}/tts-sample`, {
        voice: voiceSettings.voice,
        speed: voiceSettings.speed,
        voice_instructions: voiceSettings.voice_instructions,
        text: 'Ciao! Sono il tuo assistente vocale AI. Come posso aiutarti oggi?'
      });
      
      // apiClient returns the full response; l'audioUrl può trovarsi sotto
      // `data` oppure al top-level a seconda dello stile di envelope.
      const respAny = response as unknown as { data?: { audioUrl?: string }; audioUrl?: string };
      const audioUrlValue = respAny.data?.audioUrl || respAny.audioUrl;
      if (audioUrlValue) {
        setAudioUrl(audioUrlValue);
        toast({ title: 'Audio generato', description: 'Premi play per ascoltare' });
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Impossibile generare audio', variant: 'destructive' });
    } finally {
      setIsTestingVoice(false);
    }
  };
  
  // Load RAG analytics

  const activeProvider = providers.find(p => p.is_active);
  useEffect(() => {
    setOpenaiProjectIdInput(activeProvider?.openai_project_id || '');
  }, [activeProvider?.id, activeProvider?.openai_project_id]);
  const totalTokens = tokenUsage.reduce((sum, u) => sum + (Number(u.total_tokens) || 0), 0);
  const estimatedCost = tokenUsage.reduce((sum, u) => sum + (Number(u.estimated_cost) || 0), 0);
  const totalCost = estimatedCost;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500 dark:text-sky-400" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <EmbedderChoiceWizard
        open={needsEmbedderWizard}
        onLocked={() => { setEmbedderWizardDismissed(true); refetchEmbedderGate(); }}
        onDismiss={() => { setEmbedderWizardDismissed(true); }}
      />
      <HubShell
        icon={Bot}
        title="Dashboard AI"
        subtitle="configura e monitora l'AI"
        tabs={visibleTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        mainClassName="flex-1 overflow-auto px-6 py-4 space-y-6"
      >
      {activeTab === 'dashboard' && (
        <div className="space-y-4">
          <DashboardOverview
            activeProvider={activeProvider}
            providerStatus={providerStatus}
            agents={agents}
            chatbotAgents={chatbotAgents}
            personalAssistants={personalAssistants}
            elevenLabsAgents={elevenLabsAgents}
            geminiVoiceAgents={geminiVoiceAgents}
            aiEnabled={aiEnabled}
            setAiEnabled={setAiEnabled}
            elevenLabsAnalytics={elevenLabsAnalytics}
            setActiveTab={(tab: string) => setActiveTab(tab as TabType)}
          />
        </div>
      )}

      {activeTab === 'providers' && (
        <div className="space-y-4">
          <div className="flex gap-1 p-1 rounded-lg w-fit border border-indigo-500/30 bg-muted/50 overflow-x-auto max-w-full" role="tablist" aria-label="Provider AI">
            {(() => {
              const svcCounts: Record<string, number> = {};
              if (providerStatus?.services) {
                Object.values(providerStatus.services).forEach(p => {
                  svcCounts[p] = (svcCounts[p] || 0) + 1;
                });
              }
              return [
                { id: 'openai' as ProviderSubTab, label: 'OpenAI', color: 'from-sky-500 to-blue-600', dot: activeProvider?.is_valid ? 'bg-green-500' : 'bg-muted-foreground/40', svcCount: svcCounts['openai'] || 0 },
                { id: 'claude' as ProviderSubTab, label: 'Claude AI', color: 'from-purple-500 to-violet-600', dot: providerStatus?.claude?.active ? 'bg-green-500' : providerStatus?.claude?.configured ? 'bg-yellow-500' : 'bg-muted-foreground/40', svcCount: svcCounts['claude'] || 0 },
                { id: 'gemini' as ProviderSubTab, label: 'Gemini AI', color: 'from-blue-500 to-cyan-600', dot: providerStatus?.google?.active ? 'bg-green-500' : providerStatus?.google?.configured ? 'bg-yellow-500' : 'bg-muted-foreground/40', svcCount: svcCounts['google'] || 0 },
                { id: 'elevenlabs' as ProviderSubTab, label: 'ElevenLabs', color: 'from-emerald-500 to-green-600', dot: providerStatus?.elevenlabs?.active ? 'bg-green-500' : providerStatus?.elevenlabs?.configured ? 'bg-yellow-500' : 'bg-muted-foreground/40', svcCount: 0 },
                { id: 'custom' as ProviderSubTab, label: 'Custom', color: 'from-slate-500 to-zinc-600', dot: 'bg-muted-foreground/40', svcCount: 0 },
              ];
            })().map(sub => (
              <button
                key={sub.id}
                role="tab"
                aria-selected={providerSubTab === sub.id}
                onClick={() => setProviderSubTab(sub.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-2 ${
                  providerSubTab === sub.id
                    ? `bg-gradient-to-r ${sub.color} text-white shadow-md`
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${sub.dot}`} />
                {sub.label}
                {sub.svcCount > 0 && (
                  <span className={`text-[10px] px-1 py-0 rounded-full ${
                    providerSubTab === sub.id ? 'bg-white/20' : 'bg-muted text-muted-foreground'
                  }`}>
                    {sub.svcCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {providerSubTab === 'openai' && (
            <Card className="border-border/50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Key className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                  </div>
                  <div>
                    <CardTitle>Configurazione OpenAI API</CardTitle>
                    <CardDescription>Chiave API OpenAI per questo Tenant</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {!activeProvider && (
                  <div className="p-3 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30 text-xs text-sky-800 dark:text-sky-300 flex items-center gap-2">
                    <Key className="h-3.5 w-3.5 flex-shrink-0" />
                    Nessuna chiave OpenAI configurata. Inserisci la chiave qui sotto e salva per abilitare gli altri campi.
                  </div>
                )}

                <div className="space-y-2 p-4 bg-card rounded-lg border border-border">
                  <Label className="text-foreground">Chiave API</Label>
                  <div className="flex gap-2">
                    {activeProvider ? (
                      <>
                        <Input
                          type="password"
                          value="••••••••••••••••••••••••••••••••••••••"
                          disabled
                          className="bg-muted border-border text-foreground"
                        />
                        <Button variant="outline" size="icon" onClick={() => deleteProvider(activeProvider.id)} title="Elimina chiave">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Input
                          type="password"
                          value={newApiKey}
                          onChange={(e) => setNewApiKey(e.target.value)}
                          placeholder="sk-..."
                          className="bg-muted border-border text-foreground"
                        />
                        <Button
                          onClick={createProvider}
                          disabled={isProcessing || !newApiKey}
                          className="bg-sky-500 hover:bg-sky-600"
                        >
                          {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                          Salva Chiave
                        </Button>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-yellow-500 dark:text-yellow-400 flex items-center gap-1">
                    <Key className="h-3 w-3" />
                    {activeProvider
                      ? 'La chiave API è mascherata per sicurezza. Elimina e re-inserisci per sostituirla.'
                      : 'La chiave viene cifrata at-rest. Salva per abilitare tutti i campi sotto.'}
                  </p>
                </div>

                <div className="flex items-center justify-between p-4 bg-card rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    {activeProvider?.is_valid ? (
                      <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                    ) : (
                      <XCircle className={`h-5 w-5 ${activeProvider ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground'}`} />
                    )}
                    <div>
                      <p className="font-medium text-foreground">
                        Stato Connessione: {activeProvider ? (activeProvider.is_valid ? 'Connesso' : 'Non connesso') : 'Non configurato'}
                      </p>
                      {activeProvider?.last_tested_at && (
                        <p className="text-xs text-muted-foreground">
                          Ultimo test: {new Date(activeProvider.last_tested_at).toLocaleString('it-IT')}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => activeProvider && testProvider(activeProvider.id)}
                    disabled={!activeProvider || testingProvider === activeProvider?.id}
                    className="bg-sky-500 hover:bg-sky-600"
                  >
                    {activeProvider && testingProvider === activeProvider.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Testa Connessione
                  </Button>
                </div>

                {/* OpenAI SIP Direct Configuration */}
                <div className="mt-6 p-4 bg-card rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-4">
                    <Phone className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <h3 className="font-medium text-foreground">Configurazione SIP OpenAI (Realtime API)</h3>
                    <Badge className={`ml-auto ${activeProvider?.routing_mode === 'openai_sip' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'bg-muted text-muted-foreground'}`}>
                      {activeProvider?.routing_mode === 'openai_sip' ? 'SIP Diretto' : activeProvider?.routing_mode === 'shadow_both' ? 'Dual Stack' : 'WebSocket Legacy'}
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Project ID OpenAI</Label>
                      <div className="flex gap-2">
                        <Input
                          value={openaiProjectIdInput}
                          onChange={(e) => setOpenaiProjectIdInput(e.target.value)}
                          placeholder="proj_xxxxxxxxxxxxxxxx"
                          disabled={!activeProvider}
                          className="bg-muted border-border"
                        />
                        <Button
                          variant="outline"
                          disabled={!activeProvider}
                          onClick={() => {
                            if (!activeProvider) return;
                            apiClient.put(`/ai/providers/${activeProvider.id}`, { openai_project_id: openaiProjectIdInput })
                              .then(() => {
                                toast({ title: 'Successo', description: 'Project ID salvato' });
                                loadData();
                              })
                              .catch(() => {
                                toast({ title: 'Errore', description: 'Impossibile salvare Project ID', variant: 'destructive' });
                              });
                          }}
                        >
                          Salva
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Trova il Project ID nella console OpenAI: Settings → Project ID
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-foreground">Modalità Routing</Label>
                      <Select
                        value={activeProvider?.routing_mode || 'legacy_ws_bridge'}
                        disabled={!activeProvider}
                        onValueChange={(value) => {
                          if (!activeProvider) return;
                          apiClient.put(`/ai/providers/${activeProvider.id}`, { routing_mode: value })
                            .then(() => {
                              toast({ title: 'Successo', description: 'Modalità routing aggiornata' });
                              loadData();
                            });
                        }}
                      >
                        <SelectTrigger className="bg-muted border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="legacy_ws_bridge">WebSocket Bridge (Legacy)</SelectItem>
                          <SelectItem value="openai_sip">SIP Diretto OpenAI</SelectItem>
                          <SelectItem value="shadow_both">Dual Stack (SIP + WS fallback)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-foreground">Webhook URL (da configurare in OpenAI Console)</Label>
                      <div className="flex gap-2">
                        <Input
                          value={activeProvider?.webhook_url || ''}
                          disabled
                          placeholder={activeProvider ? '' : 'Disponibile dopo aver salvato la chiave API'}
                          className="bg-muted border-border text-foreground font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          disabled={!activeProvider?.webhook_url}
                          onClick={() => {
                            if (!activeProvider?.webhook_url) return;
                            navigator.clipboard.writeText(activeProvider.webhook_url);
                            toast({ title: 'Copiato!', description: 'Webhook URL copiato negli appunti' });
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Registra questo URL nella OpenAI Console: Settings → Webhooks → realtime.call.incoming
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border">
                      <div className="flex items-center gap-2">
                        {activeProvider?.webhook_secret_configured ? (
                          <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />
                        ) : (
                          <XCircle className={`h-4 w-4 ${activeProvider ? 'text-yellow-500 dark:text-yellow-400' : 'text-muted-foreground'}`} />
                        )}
                        <span className="text-sm text-foreground">
                          Webhook Secret: {activeProvider?.webhook_secret_configured ? 'Configurato' : 'Non configurato'}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!activeProvider}
                        onClick={() => {
                          if (!activeProvider) return;
                          const secret = prompt('Incolla il segreto dell’endpoint webhook dalla console OpenAI. Il salvataggio non genera né modifica il segreto su OpenAI:');
                          if (secret) {
                            if (!secret.trim()) {
                              toast({ title: 'Segreto non valido', description: 'Incolla il segreto dell’endpoint webhook dalla console OpenAI.', variant: 'destructive' });
                              return;
                            }
                            apiClient.put(`/ai/providers/${activeProvider.id}`, { webhook_secret: secret })
                              .then(() => {
                                toast({ title: 'Segreto salvato', description: 'La corrispondenza con il segreto dell’endpoint OpenAI non è stata verificata.' });
                                loadData();
                              })
                              .catch(() => {
                                // Never include the submitted secret or the request in feedback/logs.
                                toast({ title: 'Salvataggio non riuscito', description: 'Non è stato possibile salvare il segreto webhook. Riprova.', variant: 'destructive' });
                              });
                          }
                        }}
                      >
                        {activeProvider?.webhook_secret_configured ? 'Aggiorna' : 'Configura'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Usa il segreto dell’endpoint webhook configurato nella console OpenAI, non la chiave API.
                      “Configurato” indica solo che è salvato: la corrispondenza non è verificata e il segreto su OpenAI non viene modificato.
                    </p>
                  </div>
                </div>

                {/* Hidden Org-Level API Key Section */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowOrgKeySection(!showOrgKeySection)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  >
                    <Settings className="h-3 w-3" />
                    {showOrgKeySection ? 'Nascondi' : 'Impostazioni avanzate'}
                  </button>
                  {showOrgKeySection && (
                    <div className="mt-3 p-4 bg-card rounded-lg border border-border">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                        <h4 className="text-sm font-medium text-foreground">API Key Organizzazione</h4>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Chiave API a livello organizzazione per accedere ai costi reali, billing e altre API account-level di OpenAI.
                        Se non configurata, verrà usata la chiave progetto.
                      </p>
                      {activeProvider?.org_key_configured ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Input
                              type="password"
                              value="••••••••••••••••••••••••••••••••••••••"
                              disabled
                              className="bg-muted border-border text-foreground text-sm"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                apiClient.put(`/ai/providers/${activeProvider.id}`, { org_api_key: '' })
                                  .then(() => {
                                    toast({ title: 'Rimossa', description: 'Chiave organizzazione rimossa' });
                                    loadData();
                                  });
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Configurata ({activeProvider.org_api_key_hint})
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Input
                            type="password"
                            value={orgApiKey}
                            onChange={(e) => setOrgApiKey(e.target.value)}
                            placeholder={activeProvider ? 'sk-admin-...' : 'Disponibile dopo aver salvato la chiave API'}
                            disabled={!activeProvider}
                            className="bg-muted border-border text-sm"
                          />
                          <Button
                            size="sm"
                            disabled={!activeProvider || !orgApiKey || savingOrgKey}
                            className="bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={async () => {
                              if (!activeProvider) return;
                              setSavingOrgKey(true);
                              try {
                                await apiClient.put(`/ai/providers/${activeProvider.id}`, { org_api_key: orgApiKey });
                                toast({ title: 'Salvata', description: 'Chiave organizzazione configurata' });
                                setOrgApiKey('');
                                loadData();
                              } catch (err) {
                                toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
                              } finally {
                                setSavingOrgKey(false);
                              }
                            }}
                          >
                            {savingOrgKey && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                            Salva Chiave Org
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
          </Card>
          )}

          {providerSubTab === 'claude' && (
            <ClaudeAIConfigTab />
          )}

          {providerSubTab === 'gemini' && (
            <GeminiAIConfigTab />
          )}

          {providerSubTab === 'elevenlabs' && (
            <ElevenLabsConfigTab />
          )}

          {/* Task #1976 — Custom AI (Ollama/vLLM/RunPod/LM Studio/Together/Groq/Mistral). */}
          {providerSubTab === 'custom' && (
            <CustomAITab />
          )}
        </div>
      )}

      {activeTab === 'agents' && (
        <div className="space-y-4">
          <TenantUsageBanner tier="ai_agents" />
          <div className="flex flex-wrap gap-1 p-1 rounded-lg w-fit border border-sky-500/30 bg-muted/50" role="tablist" aria-label="Categorie Agenti AI">
            {([
              { id: 'voice-openai' as AgentSubTab, label: 'Vocali OpenAI', count: agents.length, icon: Mic, color: 'from-sky-500 to-blue-600' },
              { id: 'voice-elevenlabs' as AgentSubTab, label: 'Vocali ElevenLabs', count: elevenLabsAgents.length, icon: Volume2, color: 'from-emerald-500 to-green-600' },
              { id: 'voice-gemini' as AgentSubTab, label: 'Vocali Gemini', count: geminiVoiceAgents.length, icon: Sparkles, color: 'from-blue-500 to-indigo-600' },
              { id: 'chatbot' as AgentSubTab, label: 'Chatbot', count: chatbotAgents.length, icon: MessageSquare, color: 'from-cyan-500 to-blue-600' },
              { id: 'assistants' as AgentSubTab, label: 'Assistenti', count: personalAssistants.length, icon: Users, color: 'from-violet-500 to-purple-600' },
              { id: 'workflow' as AgentSubTab, label: 'Workflow Builder', count: 1, icon: GitBranch, color: 'from-purple-500 to-indigo-600' },
              { id: 'system' as AgentSubTab, label: 'Sistema', count: bibliotecarioSettings ? 4 : 3, icon: Sparkles, color: 'from-amber-500 to-orange-600' },
            ]).map(sub => (
              <button
                key={sub.id}
                role="tab"
                aria-selected={agentSubTab === sub.id}
                onClick={() => setAgentSubTab(sub.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  agentSubTab === sub.id
                    ? `bg-gradient-to-r ${sub.color} text-white shadow-md`
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <sub.icon className="h-3.5 w-3.5" />
                {sub.label} ({sub.count})
              </button>
            ))}
          </div>

          {agentSubTab === 'voice-openai' && (
          <div className="space-y-3">
          {/* ═══ CATEGORY 1: Agenti Vocali AI (OpenAI) ═══ */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                  <Mic className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Agenti Vocali AI</h3>
                  <p className="text-xs text-muted-foreground">Agenti vocali per chiamate SIP con OpenAI Realtime</p>
                </div>
                <Badge className="bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-800 text-[10px]">{agents.length} agenti</Badge>
                {openaiRealtimeModelsOffline && (
                  <span
                    className="text-[10px] px-1.5 py-0 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                    title="La lista modelli OpenAI Realtime proviene dal fallback locale: la chiamata API al provider non è andata a buon fine."
                  >
                    ⚠️ Lista offline — verifica chiave API
                  </span>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => {
                  setSelectedAgent(null);
                  setShowAgentDialog(true);
                }}
                className="bg-sky-600 hover:bg-sky-700 text-white h-8 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nuovo Agente
              </Button>
            </div>
            {agents.length === 0 ? (
              <Card className="border border-dashed border-border bg-muted/50">
                <CardContent className="py-8 text-center">
                  <Mic className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Nessun agente vocale configurato</p>
                  <p className="text-xs text-muted-foreground mt-1">Clicca "Nuovo Agente" per creare il tuo primo agente vocale</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2.5">
                {agents.map((agent, index) => {
                  const tags = AGENT_TAGS[String(index + 1)] || [{ label: 'General', color: 'bg-muted text-muted-foreground' }];
                  return (
                    <Card key={agent.id} className="border border-border hover:border-sky-300 dark:border-sky-700 hover:shadow-sm transition-all bg-card">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${agent.is_active ? 'bg-sky-100 dark:bg-sky-900/30' : 'bg-muted'}`}>
                              <Mic className={`h-5 w-5 ${agent.is_active ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground dark:text-muted-foreground'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-sm text-foreground truncate">{agent.name || `Agent Voice AI ${index + 1}`}</h4>
                                <Badge className={agent.is_active ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700 text-[10px]' : 'bg-muted/50 text-muted-foreground border border-border text-[10px]'}>
                                  {agent.is_active ? 'Attivo' : 'Sospeso'}
                                </Badge>
                                {!agent.template_id && (
                                  <Badge className="bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-700 text-[10px]">Custom</Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {agent.description || `Modello: ${agent.model} · Temp: ${agent.temperature}`}
                              </p>
                              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                {tags.map((tag, i) => (
                                  <Badge key={i} className="bg-muted text-muted-foreground border-0 text-[10px] px-1.5 py-0">{tag.label}</Badge>
                                ))}
                                {agent.rag_enabled && (
                                  <Badge className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 text-[10px] px-1.5 py-0">
                                    <Database className="h-2.5 w-2.5 mr-0.5" />RAG
                                  </Badge>
                                )}
                                {(agent.context_memory_enabled || agent.semantic_memory_enabled) && (
                                  <Badge className="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 text-[10px] px-1.5 py-0">
                                    <Brain className="h-2.5 w-2.5 mr-0.5" />Memoria
                                  </Badge>
                                )}
                                {agent.transcription_enabled && (
                                  <Badge className="bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-800 text-[10px] px-1.5 py-0">
                                    <FileText className="h-2.5 w-2.5 mr-0.5" />STT
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                            <Switch
                              checked={agent.is_active}
                              onCheckedChange={(checked) => toggleAgent(agent.id, checked)}
                              className="data-[state=checked]:bg-sky-500"
                            />
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRagAnalyticsAgentId(agent.id)} aria-label="RAG Analytics">
                              <PieChart className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openAgentConfig(agent)} aria-label="Impostazioni agente">
                              <Settings className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            {!agent.template_id && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpenaiAgentDeleteConfirm(agent)} aria-label="Elimina agente">
                                <Trash2 className="h-4 w-4 text-red-400 dark:text-red-300 hover:text-red-600 dark:text-red-400" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          </div>
          )}

          {agentSubTab === 'workflow' && (
          <div className="space-y-3">
          {/* ═══ CATEGORY 2: Workflow Builder Agent ═══ */}
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <GitBranch className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Workflow Builder Agent</h3>
                <p className="text-xs text-muted-foreground dark:text-muted-foreground">Creazione assistita di workflow con AI</p>
              </div>
              <Badge className="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 text-[10px]">Evergreen Template</Badge>
            </div>
            <Card className="border border-border hover:border-purple-300 dark:border-purple-700 hover:shadow-sm transition-all bg-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                      <GitBranch className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm text-foreground">Workflow Builder Agent</h4>
                        <Badge className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800 text-[10px]">Attivo</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Guida nella creazione di workflow con conversazione intelligente. Genera automaticamente i nodi nel canvas.
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Badge className="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 text-[10px] px-1.5 py-0">
                          <Zap className="h-2.5 w-2.5 mr-0.5" />Auto-generate
                        </Badge>
                        <Badge className="bg-muted text-muted-foreground border-0 text-[10px] px-1.5 py-0">
                          Usa dal canvas Action Builder
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowWorkflowBuilderModal(true)}
                      className="border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 dark:bg-purple-900/20 h-8 text-xs"
                    >
                      <Settings className="h-3.5 w-3.5 mr-1" />
                      Configura
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          </div>
          )}

          {agentSubTab === 'system' && (
          <div className="space-y-3">
            {/* ═══ Agenti di Sistema — Architetto (super-orchestrator A2A, Task #1961) ═══ */}
            <Card className="border border-indigo-200 dark:border-indigo-700 bg-gradient-to-br from-indigo-50/60 to-white dark:from-indigo-900/10 dark:to-gray-900">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <Bot className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        Architetto
                        <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 text-[10px]">Sistema · Orchestrator A2A</Badge>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Super-agente che riceve un obiettivo e delega ad altri agenti A2A o tool MCP secondo la Capability Matrix del tenant.
                      </CardDescription>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => setArchitectModalOpen(true)}>
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    Configura
                  </Button>
                </div>
              </CardHeader>
            </Card>
            {/* ═══ Agenti di Sistema — Profilazione Clienti (Task #3220) ═══ */}
            <Card className="border border-emerald-200 dark:border-emerald-700 bg-gradient-to-br from-emerald-50/60 to-white dark:from-emerald-900/10 dark:to-gray-900">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <Target className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        Profilazione Clienti
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">Sistema · Scoring missioni</Badge>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Mantiene per ogni persona uno score su acquisizione, espansione, fidelizzazione e riattivazione: base regole/pesi + aggiustamento AI bounded periodico.
                      </CardDescription>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => setProfilingModalOpen(true)}>
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    Configura
                  </Button>
                </div>
              </CardHeader>
            </Card>
            {/* ═══ Agenti di Sistema — Commissioning Agent (Task #3414) ═══ */}
            <Card className="border border-yellow-200 dark:border-yellow-700 bg-gradient-to-br from-yellow-50/60 to-white dark:from-yellow-900/10 dark:to-gray-900">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        Commissioning Agent
                        <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-200 text-[10px]">Sistema · Incentivazione</Badge>
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Assiste la creazione di gare, configuratori, functions e value packages dall&apos;hub Incentivazione; analizza le lettere gara PDF proponendo la mappatura con riuso dal catalogo (entità create in draft dopo approvazione).
                      </CardDescription>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => setCommissioningModalOpen(true)}>
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    Configura
                  </Button>
                </div>
              </CardHeader>
            </Card>
            {/* ═══ Agenti di Sistema — Bibliotecario (Company Wiki) ═══ */}
            {bibliotecarioSettings && (
              <Card className="border border-amber-200 dark:border-amber-700 bg-gradient-to-br from-amber-50/60 to-white dark:from-amber-900/10 dark:to-gray-900">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          Bibliotecario
                          <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px]">Sistema · Company Wiki</Badge>
                          <Badge className={bibliotecarioSettings.enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]' : 'bg-gray-100 text-gray-500 border border-gray-200 text-[10px]'}>
                            {bibliotecarioSettings.enabled ? 'Attivo' : 'Disattivo'}
                          </Badge>
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Agente file-based su R3 che mantiene la wiki aziendale (INDICE / pagine / fonti / cronologia). Usa scope MCP <code>mcp:wiki:read</code> + <code>mcp:wiki:write</code>.
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => setBibliotecarioModalOpen(true)}>
                        <Settings className="h-3.5 w-3.5 mr-1" />
                        Configura
                      </Button>
                      <a
                        href={bibliotecarioWikiPath}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white transition-colors"
                      >
                        <Bot className="h-3.5 w-3.5" /> Apri dashboard wiki
                      </a>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            )}
          </div>
          )}

          {agentSubTab === 'chatbot' && (
          <div className="space-y-3">
          {/* ═══ CATEGORY 3: Chatbot AI ═══ */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Chatbot AI</h3>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground">Widget embed con RAG per il tuo sito web</p>
                </div>
                <Badge className="bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800 text-[10px]">{chatbotAgents.length} chatbot</Badge>
              </div>
              <Button 
                size="sm"
                onClick={() => {
                  setSelectedChatbot(null);
                  setShowChatbotModal(true);
                }}
                className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nuovo Chatbot
              </Button>
            </div>
            {chatbotAgents.length === 0 ? (
              <Card className="border border-dashed border-border bg-muted/50">
                <CardContent className="py-8 text-center">
                  <MessageSquare className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Nessun chatbot AI configurato</p>
                  <p className="text-xs text-muted-foreground mt-1">Crea il tuo primo chatbot con knowledge base RAG</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2.5">
                {chatbotAgents.map((chatbot) => (
                  <Card key={chatbot.id} className="border border-border hover:border-cyan-300 dark:border-cyan-700 hover:shadow-sm transition-all bg-card">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm text-foreground truncate">{chatbot.name}</h4>
                              <Badge className={chatbot.is_active ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700 text-[10px]' : 'bg-muted/50 text-muted-foreground border border-border text-[10px]'}>
                                {chatbot.is_active ? 'Attivo' : 'Disattivo'}
                              </Badge>
                              {(chatbot.context_memory_enabled || chatbot.semantic_memory_enabled) && (
                                <Badge className="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 text-[10px] px-1.5 py-0">
                                  <Brain className="h-2.5 w-2.5 mr-0.5" />Memoria
                                </Badge>
                              )}
                            </div>
                            {chatbot.description && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">{chatbot.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                <FileText className="h-2.5 w-2.5 text-blue-500 dark:text-blue-400" />
                                <span className="text-blue-600 dark:text-blue-400 font-medium">{chatbot.document_count}</span> doc
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                <Link className="h-2.5 w-2.5 text-purple-500 dark:text-purple-400" />
                                <span className="text-purple-600 dark:text-purple-400 font-medium">{chatbot.url_count}</span> URL
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                <Database className="h-2.5 w-2.5 text-green-500 dark:text-green-400" />
                                <span className="text-green-600 dark:text-green-400 font-medium">{chatbot.chunk_count}</span> chunks
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                <MessageSquare className="h-2.5 w-2.5 text-orange-500 dark:text-orange-400" />
                                <span className="text-orange-600 dark:text-orange-400 font-medium">{chatbot.conversation_count}</span> conv.
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                          <Switch
                            checked={chatbot.is_active}
                            onCheckedChange={(checked) => toggleChatbot(chatbot.id, checked)}
                            className="data-[state=checked]:bg-cyan-500"
                          />
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRagAnalyticsAgentId(chatbot.id)} aria-label="RAG Analytics">
                            <PieChart className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedChatbot(chatbot); setShowChatbotModal(true); }} aria-label="Impostazioni chatbot">
                            <Settings className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEmbedChatbot(chatbot); setShowEmbedModal(true); }} aria-label="Codice embed">
                            <Code className="h-4 w-4 text-cyan-500 dark:text-cyan-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 dark:text-red-300 hover:text-red-600 dark:text-red-400" onClick={() => setChatbotDeleteConfirm({ id: chatbot.id, name: chatbot.name })} aria-label="Elimina chatbot">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          </div>
          )}

          {agentSubTab === 'assistants' && (
          <div className="space-y-3">
          {/* ═══ CATEGORY 4: Assistenti Personali (Jarvis AI) ═══ */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Assistenti Personali</h3>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground">Jarvis AI con RAG, MCP tools e memoria persistente</p>
                </div>
                <Badge className="bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-800 text-[10px]">{personalAssistants.length} assistenti</Badge>
              </div>
              <Button 
                size="sm"
                onClick={() => {
                  setSelectedAssistant(null);
                  setShowAssistantModal(true);
                }}
                className="bg-violet-600 hover:bg-violet-700 text-white h-8 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nuovo Assistente
              </Button>
            </div>
            {personalAssistants.length === 0 ? (
              <Card className="border border-dashed border-border bg-muted/50">
                <CardContent className="py-8 text-center">
                  <Sparkles className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Nessun assistente personale configurato</p>
                  <p className="text-xs text-muted-foreground mt-1">Crea il tuo primo assistente AI privato</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2.5">
                {personalAssistants.map((pa) => {
                  const iconMap: Record<string, React.ReactNode> = {
                    bot: <Bot className="h-5 w-5" />,
                    sparkles: <Sparkles className="h-5 w-5" />,
                    brain: <Brain className="h-5 w-5" />,
                    zap: <Zap className="h-5 w-5" />,
                    shield: <Shield className="h-5 w-5" />,
                    star: <Star className="h-5 w-5" />
                  };
                  return (
                    <Card key={pa.id} className="border border-border hover:border-violet-300 dark:border-violet-700 hover:shadow-sm transition-all bg-card">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pa.color_theme || '#8B5CF6'}15`, color: pa.color_theme || '#8B5CF6' }}>
                              {iconMap[pa.avatar_icon || 'sparkles'] || <Sparkles className="h-5 w-5" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-sm text-foreground truncate">{pa.name}</h4>
                                <Badge className={pa.is_active ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700 text-[10px]' : 'bg-muted/50 text-muted-foreground border border-border text-[10px]'}>
                                  {pa.is_active ? 'Attivo' : 'Disattivo'}
                                </Badge>
                                {pa.assigned_user_name && (
                                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded border border-border">
                                    <UserCircle className="h-2.5 w-2.5" />
                                    {pa.assigned_user_name}
                                  </div>
                                )}
                              </div>
                              {pa.description && (
                                <p className="text-xs text-muted-foreground truncate mt-0.5">{pa.description}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                  <FileText className="h-2.5 w-2.5 text-blue-500 dark:text-blue-400" />
                                  <span className="text-blue-600 dark:text-blue-400 font-medium">{pa.document_count}</span> doc
                                </div>
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                  <Link className="h-2.5 w-2.5 text-purple-500 dark:text-purple-400" />
                                  <span className="text-purple-600 dark:text-purple-400 font-medium">{pa.url_count}</span> URL
                                </div>
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                  <Database className="h-2.5 w-2.5 text-green-500 dark:text-green-400" />
                                  <span className="text-green-600 dark:text-green-400 font-medium">{pa.chunk_count}</span> chunks
                                </div>
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                  <MessageSquare className="h-2.5 w-2.5 text-orange-500 dark:text-orange-400" />
                                  <span className="text-orange-600 dark:text-orange-400 font-medium">{pa.conversation_count}</span> conv.
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                            <Switch
                              checked={pa.is_active}
                              onCheckedChange={(checked) => toggleAssistant(pa.id, checked)}
                              className="data-[state=checked]:bg-violet-500"
                            />
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRagAnalyticsAgentId(pa.id)} aria-label="RAG Analytics">
                              <PieChart className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedAssistant(pa); setShowAssistantModal(true); }} aria-label="Impostazioni assistente">
                              <Settings className="h-4 w-4 text-muted-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-400 dark:text-red-300 hover:text-red-600 dark:text-red-400"
                              aria-label="Elimina assistente"
                              onClick={async () => {
                                if (!confirm('Eliminare questo assistente?')) return;
                                try {
                                  await apiClient.delete(`/personal-assistant/agents/${pa.id}`);
                                  toast({ title: 'Assistente eliminato' });
                                  setPersonalAssistants(prev => prev.filter(a => a.id !== pa.id));
                                } catch (err) {
                                  toast({ title: 'Errore', description: 'Impossibile eliminare', variant: 'destructive' });
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          </div>
          )}

          {agentSubTab === 'voice-elevenlabs' && (
          <div className="space-y-3">
          {/* ═══ CATEGORY 5: ElevenLabs Voice Agents ═══ */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Volume2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">ElevenLabs Voice Agents</h3>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground">Agenti vocali con supporto SIP e WebRTC dual-channel</p>
                </div>
                <Badge className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 text-[10px]">{elevenLabsAgents.length} agenti</Badge>
              </div>
              <Button 
                size="sm"
                onClick={() => {
                  setSelectedElevenLabsAgent(null);
                  setShowElevenLabsModal(true);
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nuovo Agente
              </Button>
            </div>
            {elevenLabsAgents.length === 0 ? (
              <Card className="border border-dashed border-border bg-muted/50">
                <CardContent className="py-8 text-center">
                  <Volume2 className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Nessun agente ElevenLabs configurato</p>
                  <p className="text-xs text-muted-foreground mt-1">Crea il tuo primo agente vocale con voce personalizzata</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2.5">
                {elevenLabsAgents.map((elAgent) => (
                  <Card key={elAgent.id} className="border border-border hover:border-emerald-300 dark:border-emerald-700 hover:shadow-sm transition-all bg-card">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                            <Volume2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm text-foreground truncate">{elAgent.name}</h4>
                              <Badge className={elAgent.is_active ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700 text-[10px]' : 'bg-muted/50 text-muted-foreground border border-border text-[10px]'}>
                                {elAgent.is_active ? 'Attivo' : 'Disattivo'}
                              </Badge>
                            </div>
                            {elAgent.description && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">{elAgent.description}</p>
                            )}
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              {elAgent.channel_sip && (
                                <Badge className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 text-[10px] px-1.5 py-0">
                                  <Phone className="h-2.5 w-2.5 mr-0.5" />SIP
                                </Badge>
                              )}
                              {elAgent.channel_webrtc && (
                                <Badge className="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 text-[10px] px-1.5 py-0">
                                  <Globe className="h-2.5 w-2.5 mr-0.5" />WebRTC
                                </Badge>
                              )}
                              {elAgent.voice_name && (
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                  <Volume2 className="h-2.5 w-2.5 text-emerald-500 dark:text-emerald-400" />
                                  {elAgent.voice_name}
                                </div>
                              )}
                              <Badge className="bg-muted/50 text-muted-foreground border border-border text-[10px] px-1.5 py-0">
                                {elAgent.llm_provider === 'openai' ? 'OpenAI' : 
                                 elAgent.llm_provider === 'claude' ? 'Claude' :
                                 elAgent.llm_provider === 'gemini' ? 'Gemini' :
                                 elAgent.llm_provider === 'elevenlabs' ? 'ElevenLabs' : 'Custom'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                          <Switch
                            checked={elAgent.is_active}
                            onCheckedChange={(checked) => toggleElevenLabsAgent(elAgent.id, checked)}
                            className="data-[state=checked]:bg-emerald-500"
                          />
                          {elAgent.channel_webrtc && elAgent.elevenlabs_agent_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-emerald-500 dark:text-emerald-400 hover:text-emerald-700 dark:text-emerald-300"
                              aria-label="Test WebRTC Widget"
                              onClick={() => {
                                const agentId = elAgent.elevenlabs_agent_id;
                                const agentName = elAgent.name;
                                const htmlContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Test: ${agentName}</title><script src="https://unpkg.com/@elevenlabs/convai-widget-embed" async type="text/javascript"><\/script><style>body{margin:0;font-family:system-ui,sans-serif;background:#111827;color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh}.header{text-align:center;padding:20px}.header h2{margin:0 0 8px;font-size:18px}.header p{margin:0;color:#9ca3af;font-size:13px}.badge{display:inline-block;background:#059669;color:#fff;padding:2px 8px;border-radius:12px;font-size:11px;margin-top:8px}</style></head><body><div class="header"><h2>${agentName}</h2><p>EDG VoIP - Test WebRTC</p><span class="badge">ElevenLabs</span></div><elevenlabs-convai agent-id="${agentId}"></elevenlabs-convai></body></html>`;
                                const blob = new Blob([htmlContent], { type: 'text/html' });
                                const url = URL.createObjectURL(blob);
                                window.open(url, `el_test_${elAgent.id}`, 'width=420,height=650,toolbar=no,menubar=no,scrollbars=no,resizable=yes');
                              }}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRagAnalyticsAgentId(elAgent.id)} aria-label="RAG Analytics">
                            <PieChart className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedElevenLabsAgent(elAgent); setShowElevenLabsModal(true); }} aria-label="Impostazioni ElevenLabs">
                            <Settings className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-400 dark:text-red-300 hover:text-red-600 dark:text-red-400"
                            aria-label="Elimina agente ElevenLabs"
                            onClick={async () => {
                              if (!confirm('Eliminare questo agente ElevenLabs?')) return;
                              try {
                                await apiClient.delete(`/elevenlabs/agents/${elAgent.id}`);
                                toast({ title: 'Agente eliminato' });
                                setElevenLabsAgents(prev => prev.filter(a => a.id !== elAgent.id));
                              } catch (err) {
                                toast({ title: 'Errore', description: 'Impossibile eliminare', variant: 'destructive' });
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          </div>
          )}

          {agentSubTab === 'voice-gemini' && (
          <div className="space-y-3">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Gemini Live Voice Agents</h3>
                  <p className="text-xs text-muted-foreground">Agenti vocali con Google Gemini Live API — audio nativo bidirezionale</p>
                </div>
                <Badge className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 text-[10px]">{geminiVoiceAgents.length} agenti</Badge>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  setSelectedGeminiVoiceAgent(null);
                  setShowGeminiVoiceModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Nuovo Agente
              </Button>
            </div>
            {geminiVoiceAgents.length === 0 ? (
              <Card className="border border-dashed border-border bg-muted/50">
                <CardContent className="py-8 text-center">
                  <Sparkles className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Nessun agente Gemini Voice configurato</p>
                  <p className="text-xs text-muted-foreground mt-1">Crea il tuo primo agente vocale con Gemini Live API</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-2.5">
                {geminiVoiceAgents.map((gvAgent) => (
                  <Card key={gvAgent.id} className="border border-border hover:border-blue-300 dark:border-blue-700 hover:shadow-sm transition-all bg-card">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-sm text-foreground truncate">{gvAgent.name}</h4>
                              <Badge className={gvAgent.is_active ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700 text-[10px]' : 'bg-muted/50 text-muted-foreground border border-border text-[10px]'}>
                                {gvAgent.is_active ? 'Attivo' : 'Disattivo'}
                              </Badge>
                            </div>
                            {gvAgent.description && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">{gvAgent.description}</p>
                            )}
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                              {gvAgent.channel_sip && (
                                <Badge className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 text-[10px] px-1.5 py-0">
                                  <Phone className="h-2.5 w-2.5 mr-0.5" />SIP
                                </Badge>
                              )}
                              {gvAgent.channel_webrtc && (
                                <Badge className="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 text-[10px] px-1.5 py-0">
                                  <Globe className="h-2.5 w-2.5 mr-0.5" />WebRTC
                                </Badge>
                              )}
                              <div className="flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                                <Volume2 className="h-2.5 w-2.5 text-blue-500 dark:text-blue-400" />
                                {gvAgent.voice}
                              </div>
                              <Badge className="bg-muted/50 text-muted-foreground border border-border text-[10px] px-1.5 py-0">
                                {gvAgent.model.includes('2.5') ? 'Gemini 2.5' : 'Gemini 2.0'}
                              </Badge>
                              {gvAgent.google_search_enabled && (
                                <Badge className="bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 text-[10px] px-1.5 py-0">
                                  <Search className="h-2.5 w-2.5 mr-0.5" />Search
                                </Badge>
                              )}
                              {gvAgent.rag_enabled && (
                                <Badge className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 text-[10px] px-1.5 py-0">
                                  <FileText className="h-2.5 w-2.5 mr-0.5" />RAG
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                          <Switch
                            checked={gvAgent.is_active}
                            onCheckedChange={async (checked) => {
                              try {
                                await apiClient.patch(`/gemini-voice/agents/${gvAgent.id}/toggle`, { is_active: checked });
                                setGeminiVoiceAgents(prev => prev.map(a => a.id === gvAgent.id ? { ...a, is_active: checked } : a));
                              } catch (err) {
                                toast({ title: 'Errore toggle', variant: 'destructive' });
                              }
                            }}
                            className="data-[state=checked]:bg-blue-500"
                          />
                          {gvAgent.channel_webrtc && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:text-blue-300"
                              aria-label="Test WebRTC Widget"
                              onClick={() => {
                                const params = new URLSearchParams({
                                  color: gvAgent.webrtc_widget_config?.color || '#4285f4',
                                  position: gvAgent.webrtc_widget_config?.position || 'bottom-right',
                                  cta: gvAgent.webrtc_widget_config?.cta_text || 'Parla con noi',
                                  name: gvAgent.name
                                });
                                const testUrl = `${window.location.origin}/api/widgets/gemini-voice/test/${gvAgent.id}?${params.toString()}`;
                                window.open(testUrl, `gv_test_${gvAgent.id}`, 'width=420,height=650,toolbar=no,menubar=no,scrollbars=no,resizable=yes');
                              }}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRagAnalyticsAgentId(gvAgent.id)} aria-label="RAG Analytics">
                            <PieChart className="h-4 w-4 text-purple-500 dark:text-purple-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedGeminiVoiceAgent(gvAgent); setShowGeminiVoiceModal(true); }}>
                            <Settings className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-400 dark:text-red-300 hover:text-red-600 dark:text-red-400"
                            onClick={async () => {
                              if (!confirm('Eliminare questo agente Gemini Voice?')) return;
                              try {
                                await apiClient.delete(`/gemini-voice/agents/${gvAgent.id}`);
                                toast({ title: 'Agente eliminato' });
                                setGeminiVoiceAgents(prev => prev.filter(a => a.id !== gvAgent.id));
                              } catch (err) {
                                toast({ title: 'Errore', description: 'Impossibile eliminare', variant: 'destructive' });
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          </div>
          )}
        </div>
      )}

      {analyticsTabVisited && (
        <div style={{ display: activeTab === 'analytics' ? 'block' : 'none' }}>
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-sky-500 dark:text-sky-400" />
            </div>
          }>
            <AnalyticsDashboard />
          </Suspense>
        </div>
      )}

      {(activeTab as string) === '_analytics_old_disabled' && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 p-1 rounded-lg border border-border w-fit">
            {(['all', 'openai', 'elevenlabs'] as const).map((prov) => (
              <button
                key={prov}
                onClick={() => setAnalyticsProvider(prov)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  analyticsProvider === prov
                    ? prov === 'openai'
                      ? 'bg-sky-500 text-white shadow'
                      : prov === 'elevenlabs'
                        ? 'bg-emerald-500 text-white shadow'
                        : 'bg-foreground text-background shadow'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {prov === 'all' ? 'Tutti i Provider' : prov === 'openai' ? 'OpenAI' : 'ElevenLabs'}
              </button>
            ))}
          </div>

          {analyticsProvider === 'elevenlabs' ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Phone className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Conversazioni</p>
                        <p className="text-2xl font-bold">{elevenLabsAnalytics?.stats?.total_conversations || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Durata Totale</p>
                        <p className="text-2xl font-bold">{(() => {
                          const secs = Number(elevenLabsAnalytics?.stats?.total_duration) || 0;
                          const h = Math.floor(secs / 3600);
                          const m = Math.floor((secs % 3600) / 60);
                          return h > 0 ? `${h}h ${m}m` : `${m}m`;
                        })()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <Activity className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Durata Media</p>
                        <p className="text-2xl font-bold">{(() => {
                          const secs = Number(elevenLabsAnalytics?.stats?.avg_duration) || 0;
                          return `${Math.round(secs)}s`;
                        })()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Costo Stimato</p>
                        <p className="text-2xl font-bold">${(() => {
                          const mins = (Number(elevenLabsAnalytics?.stats?.total_duration) || 0) / 60;
                          return (mins * 0.09).toFixed(2);
                        })()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Completate</p>
                        <p className="text-2xl font-bold">
                          {elevenLabsAnalytics?.stats?.completed || 0}
                          <span className="text-sm text-muted-foreground ml-1">
                            / {elevenLabsAnalytics?.stats?.failed || 0} err
                          </span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {(elevenLabsAnalytics?.daily?.length || 0) > 0 && (
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                      Conversazioni per Giorno
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">!
                      {elevenLabsAnalytics!.daily.slice(0, 14).map((day) => {
                        const maxCount = Math.max(...elevenLabsAnalytics!.daily.map(d => Number(d.count))!);
                        const pct = maxCount > 0 ? (Number(day.count) / maxCount) * 100 : 0;
                        const durMin = Math.round(Number(day.total_duration) / 60);
                        return (
                          <div key={day.date} className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground w-24 shrink-0">
                              {new Date(day.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                            </span>
                            <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500 rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-foreground w-16 text-right">{day.count} conv</span>
                            <span className="text-xs text-muted-foreground w-16 text-right">{durMin}m</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                    Conversazioni Recenti
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(elevenLabsAnalytics?.recent?.length || 0) === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Phone className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Nessuna conversazione registrata</p>
                      <p className="text-sm">I dati appariranno quando gli agenti ElevenLabs riceveranno chiamate</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b border-border">
                            <th className="pb-2">Data/Ora</th>
                            <th className="pb-2">Agente</th>
                            <th className="pb-2">Canale</th>
                            <th className="pb-2">Durata</th>
                            <th className="pb-2">Lingua</th>
                            <th className="pb-2">Stato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {elevenLabsAnalytics!.recent.slice(0, 20).map((conv) => (
                            <tr key={conv.id} className="border-b border-border/50">
                              <td className="py-2">{new Date(conv.created_at).toLocaleString('it-IT')}</td>
                              <td className="py-2">{conv.agent_name || '-'}</td>
                              <td className="py-2">
                                <Badge className={conv.channel === 'sip' ? 'bg-blue-500/20 text-blue-400 dark:text-blue-300' : 'bg-purple-500/20 text-purple-400 dark:text-purple-300'}>
                                  {conv.channel === 'sip' ? 'SIP' : 'WebRTC'}
                                </Badge>
                              </td>
                              <td className="py-2">{conv.duration_seconds || 0}s</td>
                              <td className="py-2">{conv.detected_language || '-'}</td>
                              <td className="py-2">
                                <Badge className={conv.status === 'completed' ? 'bg-green-500/20 text-green-400 dark:text-green-300' : conv.status === 'failed' ? 'bg-red-500/20 text-red-400 dark:text-red-300' : 'bg-gray-500/20 text-muted-foreground'}>
                                  {conv.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
          <>
          <div className={`grid grid-cols-1 gap-4 ${elevenLabsAnalytics?.stats ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Richieste Totali</p>
                    <p className="text-2xl font-bold">{((tokenUsage.reduce((sum, u) => sum + (Number(u.request_count) || 0), 0)) + (Number(elevenLabsAnalytics?.stats?.total_conversations) || 0)).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Activity className="h-5 w-5 text-green-500 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Token Utilizzati</p>
                    <p className="text-2xl font-bold">{totalTokens.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Costo Stimato</p>
                    <p className="text-2xl font-bold">${(totalCost + ((Number(elevenLabsAnalytics?.stats?.total_duration) || 0) / 60 * 0.09)).toFixed(2)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <PieChart className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Servizi AI Attivi</p>
                    <p className="text-2xl font-bold">{categoryBreakdown.length + (elevenLabsAnalytics?.stats?.total_conversations! > 0 ? 1 : 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {elevenLabsAnalytics?.stats && (
              <Card className="border-border/50 border-l-2 border-l-emerald-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Mic className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">ElevenLabs</p>
                      <p className="text-2xl font-bold">{elevenLabsAnalytics.stats.total_conversations || 0}</p>
                      <p className="text-xs text-muted-foreground">${((Number(elevenLabsAnalytics.stats.total_duration) || 0) / 60 * 0.09).toFixed(2)} costo</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {categoryBreakdown.length > 0 && (
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-sky-500 dark:text-sky-400" />
                  Utilizzo per Servizio AI
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <button
                    onClick={() => setAnalyticsFilter('all')}
                    className={`p-3 rounded-lg border text-left transition-all ${analyticsFilter === 'all' ? 'border-sky-500 bg-sky-500/10' : 'border-border bg-card hover:border-border'}`}
                  >
                    <p className="text-xs text-muted-foreground">Tutti</p>
                    <p className="text-lg font-bold text-foreground">{categoryBreakdown.reduce((s, c) => s + Number(c.request_count), 0)}</p>
                    <p className="text-xs text-muted-foreground">richieste</p>
                  </button>
                  {categoryBreakdown.map(cat => {
                    const cfg = CATEGORY_CONFIG[cat.category] || { label: cat.category, color: 'text-muted-foreground', bgColor: 'bg-gray-500/20' };
                    return (
                      <button
                        key={cat.category}
                        onClick={() => setAnalyticsFilter(cat.category)}
                        className={`p-3 rounded-lg border text-left transition-all ${analyticsFilter === cat.category ? 'border-sky-500 bg-sky-500/10' : 'border-border bg-card hover:border-border'}`}
                      >
                        <p className={`text-xs ${cfg.color}`}>{cfg.label}</p>
                        <p className="text-lg font-bold text-foreground">{Number(cat.request_count).toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">{Number(cat.total_tokens).toLocaleString()} token</p>
                      </button>
                    );
                  })}
                </div>

                {(() => {
                  const totalReqs = categoryBreakdown.reduce((s, c) => s + Number(c.request_count), 0);
                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-yellow-500', 'bg-teal-500'];
                  return (
                    <div className="space-y-2">
                      <div className="h-4 rounded-full overflow-hidden flex bg-muted">
                        {categoryBreakdown.map((cat, i) => {
                          const pct = totalReqs > 0 ? (Number(cat.request_count) / totalReqs) * 100 : 0;
                          return pct > 0 ? (
                            <div
                              key={cat.category}
                              className={`${colors[i % colors.length]} transition-all`}
                              style={{ width: `${pct}%` }}
                              title={`${CATEGORY_CONFIG[cat.category]?.label || cat.category}: ${pct.toFixed(1)}%`}
                            />
                          ) : null;
                        })}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs">
                        {categoryBreakdown.map((cat, i) => {
                          const cfg = CATEGORY_CONFIG[cat.category] || { label: cat.category };
                          const pct = totalReqs > 0 ? (Number(cat.request_count) / totalReqs) * 100 : 0;
                          return (
                            <div key={cat.category} className="flex items-center gap-1.5">
                              <div className={`w-2.5 h-2.5 rounded-full ${colors[i % colors.length]}`} />
                              <span className="text-muted-foreground">{cfg.label} ({pct.toFixed(1)}%)</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-sky-500 dark:text-sky-400" />
                Utilizzo per Agente / Servizio
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const filteredUsage = analyticsFilter === 'all'
                  ? tokenUsage
                  : tokenUsage.filter(u => u.request_type === analyticsFilter || u.category === analyticsFilter);

                if (filteredUsage.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Nessun dato per questa categoria</p>
                      <p className="text-sm">I dati appariranno quando verranno effettuate richieste AI</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {filteredUsage.map((usage) => {
                      const catCfg = CATEGORY_CONFIG[usage.request_type || ''] || { label: '', color: '', bgColor: '' };
                      return (
                        <div key={usage.agent_id} className="p-4 bg-card rounded-lg border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground">{usage.agent_name}</span>
                              {usage.request_type && (
                                <Badge className={`${catCfg.bgColor || 'bg-muted'} ${catCfg.color || 'text-muted-foreground'} text-xs`}>
                                  {catCfg.label || usage.request_type}
                                </Badge>
                              )}
                            </div>
                            <span className="text-sm text-muted-foreground">{Number(usage.request_count || 0).toLocaleString()} richieste</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Token: {(Number(usage.total_tokens) || 0).toLocaleString()}</span>
                            <span className="text-green-600 dark:text-green-400">${(Number(usage.estimated_cost) || 0).toFixed(4)}</span>
                          </div>
                          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-sky-500 rounded-full"
                              style={{ width: `${Math.min((Number(usage.total_tokens) / Math.max(totalTokens, 1)) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                Costo per Servizio
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const costByCategory: Record<string, number> = {};
                tokenUsage.forEach(u => {
                  const cat = u.request_type || u.category || 'voice_call';
                  costByCategory[cat] = (costByCategory[cat] || 0) + Number(u.estimated_cost || 0);
                });
                const entries = Object.entries(costByCategory).sort((a, b) => b[1] - a[1]);
                if (entries.length === 0) {
                  return <p className="text-center py-4 text-muted-foreground">Nessun dato costi disponibile</p>;
                }
                return (
                  <div className="space-y-3">
                    {entries.map(([cat, cost]) => {
                      const cfg = CATEGORY_CONFIG[cat] || { label: cat, color: 'text-muted-foreground', bgColor: 'bg-muted' };
                      return (
                        <div key={cat} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                          <div className="flex items-center gap-2">
                            <Badge className={`${cfg.bgColor} ${cfg.color} text-xs`}>{cfg.label}</Badge>
                          </div>
                          <span className="font-semibold text-foreground">${cost.toFixed(4)}</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border font-bold">
                      <span className="text-foreground">Totale</span>
                      <span className="text-foreground">${entries.reduce((s, [, c]) => s + c, 0).toFixed(4)}</span>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-sky-500 dark:text-sky-400" />
                Log Richieste Recenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              {requestLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Nessuna richiesta recente</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border">
                        <th className="pb-2">Data/Ora</th>
                        <th className="pb-2">Agente</th>
                        <th className="pb-2">Tipo</th>
                        <th className="pb-2">Modello</th>
                        <th className="pb-2">Token</th>
                        <th className="pb-2">Tempo</th>
                        <th className="pb-2">Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requestLogs
                        .filter(log => analyticsFilter === 'all' || log.request_type === analyticsFilter)
                        .slice(0, 15)
                        .map((log) => {
                          const catCfg = CATEGORY_CONFIG[log.request_type || ''] || { label: '', color: '', bgColor: '' };
                          return (
                            <tr key={log.id} className="border-b border-border/50">
                              <td className="py-2">{new Date(log.created_at).toLocaleString('it-IT')}</td>
                              <td className="py-2">{log.agent_name}</td>
                              <td className="py-2">
                                {log.request_type && (
                                  <Badge className={`${catCfg.bgColor || 'bg-muted'} ${catCfg.color || 'text-muted-foreground'} text-xs`}>
                                    {catCfg.label || log.request_type}
                                  </Badge>
                                )}
                              </td>
                              <td className="py-2">{log.model}</td>
                              <td className="py-2">{log.total_tokens || log.tokens_used || 0}</td>
                              <td className="py-2">{log.latency_ms || log.response_time_ms || 0}ms</td>
                              <td className="py-2">
                                <Badge className={log.status === 'success' ? 'bg-green-500/20 text-green-400 dark:text-green-300' : 'bg-red-500/20 text-red-400 dark:text-red-300'}>
                                  {log.status}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          </>
          )}
        </div>
      )}

      {activeTab === 'rag-analytics' && (
        <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>}>
          <RAGAnalytics />
        </Suspense>
      )}

      {activeTab === 'agent-bindings' && isAdmin && (
        <AgentBindingsPanel />
      )}

      {activeTab === 'routing' && (
        <RoutingMatrixProvider>
          <ServiceRoutingTab />
        </RoutingMatrixProvider>
      )}

      {activeTab === 'transcriptions' && (
        <TranscriptionsDashboard />
      )}

      {activeTab === 'health-check' && (
        <AIHealthCheck />
      )}

      {activeTab === 'kb-metadata' && (
        <KBMetadataSchemaPage />
      )}

      {activeTab === 'a2a-gateway' && (
        <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Caricamento A2A Gateway…</div>}>
          <A2AGatewayTab />
        </Suspense>
      )}
      </HubShell>

      <OpenAIVoiceAgentModal
        open={showAgentDialog}
        onOpenChange={setShowAgentDialog}
        agent={selectedAgent as OpenAIVoiceAgentType | null}
        onSaved={loadData}
        activeProvider={activeProvider}
        extensions={extensions}
        ringGroups={ringGroups}
      />

      
      {ragAnalyticsAgentId && (
        <RAGAnalyticsDashboard
          agentId={ragAnalyticsAgentId}
          open={!!ragAnalyticsAgentId}
          onOpenChange={(open) => { if (!open) setRagAnalyticsAgentId(null); }}
        />
      )}

      {/* Chatbot Delete Confirmation Dialog */}
      <Dialog open={!!chatbotDeleteConfirm} onOpenChange={() => setChatbotDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
              Conferma Eliminazione
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare il chatbot:
              <span className="font-medium text-foreground block mt-2 truncate">"{chatbotDeleteConfirm?.name}"</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Questa azione eliminerà permanentemente il chatbot, tutti i documenti, URL e chunks della Knowledge Base associati.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setChatbotDeleteConfirm(null)} disabled={isChatbotDeleting}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={async () => {
                if (!chatbotDeleteConfirm) return;
                setIsChatbotDeleting(true);
                try {
                  await apiClient.delete(`/chatbot/agents/${chatbotDeleteConfirm.id}`);
                  toast({ title: 'Chatbot eliminato', description: `"${chatbotDeleteConfirm.name}" è stato eliminato con successo` });
                  setChatbotAgents(prev => prev.filter(c => c.id !== chatbotDeleteConfirm.id));
                  setChatbotDeleteConfirm(null);
                } catch (err) {
                  const errMsg = (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || '';
                  if (errMsg.toLowerCase().includes('not found')) {
                    setChatbotAgents(prev => prev.filter(c => c.id !== chatbotDeleteConfirm.id));
                    setChatbotDeleteConfirm(null);
                    toast({ title: 'Chatbot rimosso', description: 'L\'agente era già stato eliminato' });
                  } else {
                    toast({ title: 'Errore', description: 'Impossibile eliminare il chatbot', variant: 'destructive' });
                  }
                } finally {
                  setIsChatbotDeleting(false);
                }
              }}
              disabled={isChatbotDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isChatbotDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Elimina
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OpenAI Agent Delete Confirmation Dialog */}
      <Dialog open={!!openaiAgentDeleteConfirm} onOpenChange={() => setOpenaiAgentDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400 dark:text-red-300">
              <AlertTriangle className="h-5 w-5" />
              Conferma Eliminazione
            </DialogTitle>
            <DialogDescription>
              Stai per eliminare l'agente vocale:
              <span className="font-medium text-foreground block mt-2 truncate">"{openaiAgentDeleteConfirm?.name}"</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Questa azione eliminerà permanentemente l'agente, tutti i documenti, URL e chunks della Knowledge Base associati.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenaiAgentDeleteConfirm(null)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              disabled={isDeletingAgent}
              onClick={async () => {
                if (!openaiAgentDeleteConfirm) return;
                setIsDeletingAgent(true);
                try {
                  await apiClient.delete(`/ai/agents/${openaiAgentDeleteConfirm.id}`);
                  setAgents(prev => prev.filter(a => a.id !== openaiAgentDeleteConfirm.id));
                  toast({ title: 'Agente eliminato', description: 'L\'agente vocale è stato rimosso' });
                  setOpenaiAgentDeleteConfirm(null);
                } catch (error: unknown) {
                  const errMsg = error instanceof Error ? error.message : 'Errore durante l\'eliminazione';
                  toast({ title: 'Errore', description: errMsg, variant: 'destructive' });
                } finally {
                  setIsDeletingAgent(false);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {isDeletingAgent ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow Builder Agent Modal */}
      <WorkflowBuilderAgentModal 
        open={showWorkflowBuilderModal} 
        onOpenChange={setShowWorkflowBuilderModal} 
      />

      {/* Commissioning Agent Config Modal (Task #3414) */}
      <CommissioningAgentConfigModal
        open={commissioningModalOpen}
        onOpenChange={setCommissioningModalOpen}
      />

      {/* Chatbot Config Modal.
          boundary: `ChatbotAgent` è duplicato divergente tra questa pagina e
          il modal; conversione esplicita al tipo atteso dal componente. */}
      <ChatbotConfigModal
        open={showChatbotModal}
        onOpenChange={setShowChatbotModal}
        chatbot={selectedChatbot as unknown as ComponentProps<typeof ChatbotConfigModal>['chatbot']}
        onSave={() => {
          loadData();
        }}
        extensions={extensions}
        queues={queues}
        trunks={trunks}
        voiceAgents={voiceAgentsForCall}
        campaigns={campaigns}
      />

      {/* Chatbot Embed Modal */}
      {embedChatbot && (
        <ChatbotEmbedModal
          open={showEmbedModal}
          onOpenChange={setShowEmbedModal}
          chatbotId={embedChatbot.id}
          chatbotName={embedChatbot.name}
          widgetConfig={embedChatbot.widget_config}
        />
      )}

      {/* boundary: `PersonalAssistantItem` (pagina) vs `PersonalAssistant`
          (modal) sono shape divergenti; conversione esplicita. */}
      <PersonalAssistantConfigModal
        open={showAssistantModal}
        onOpenChange={setShowAssistantModal}
        assistant={selectedAssistant as unknown as ComponentProps<typeof PersonalAssistantConfigModal>['assistant']}
        onSave={() => {
          loadData();
        }}
      />

      <ElevenLabsAgentConfigModal
        open={showElevenLabsModal}
        onOpenChange={setShowElevenLabsModal}
        agent={selectedElevenLabsAgent}
        onSaved={() => {
          loadData();
        }}
      />

      <GeminiVoiceAgentModal
        open={showGeminiVoiceModal}
        onOpenChange={setShowGeminiVoiceModal}
        agent={selectedGeminiVoiceAgent}
        onSaved={() => {
          loadData();
        }}
      />

      {architectModalOpen && (
        <Suspense fallback={null}>
          <ArchitectModal open={architectModalOpen} onClose={() => setArchitectModalOpen(false)} />
        </Suspense>
      )}

      {profilingModalOpen && (
        <Suspense fallback={null}>
          <ProfilingAgentConfigModal open={profilingModalOpen} onClose={() => setProfilingModalOpen(false)} />
        </Suspense>
      )}

      {bibliotecarioModalOpen && bibliotecarioSettings && (
        <Suspense fallback={null}>
          <BibliotecarioModal
            open={bibliotecarioModalOpen}
            onClose={() => setBibliotecarioModalOpen(false)}
            settings={bibliotecarioSettings}
            wikiPath={bibliotecarioWikiPath}
            onSaved={(next) => setBibliotecarioSettings(next)}
          />
        </Suspense>
      )}
    </div>
  );
}

